<script setup>
// =============================================================================
// FangatePanel.vue — orchestrator for the Fangate side panel
//
// Right-side panel (Col 4) — UI for the Fangate platform integration.
// Single-view layout (Sales tab dropped); Content tab is the whole body.
//
// Component tree (this file = the orchestrator):
//
//   FangatePanel
//   ├── FangatePanelHeader           components/FangatePanelHeader.vue
//   ├── FangateConnectBanner         components/FangateConnectBanner.vue
//   ├── FangateContentToolbar        components/FangateContentToolbar.vue
//   ├── FangateProductCard (×N)      components/FangateProductCard.vue
//   ├── FangateLinkMenu              components/FangateLinkMenu.vue
//   ├── FangatePriceModal            components/FangatePriceModal.vue   (real API)
//   └── FangateUploadModal           components/FangateUploadModal.vue  (simulated)
//
//   composables/useFangateMoney      currency-aware money + symbol formatters
//
// What the orchestrator owns:
//   • Inbox-scoped token wiring (FangateAPI.setActiveInbox)
//   • The products list + per-product variant tracking
//     (productLinks, activeLinkIdx, linkSelectorFor, mergeVariantsFromMap)
//   • Filter / sort / search state (passed via v-model to the toolbar)
//   • Loaders: loadProducts / loadCurrency / loadProfile
//   • Lifecycle: outside-click for the link-variant menu, inbox-change reloads
//
// What lives in child components:
//   • Form state, validation, API calls inside each modal
//   • Dropdown open-states + outside-click in the toolbar
//   • Locale-aware money formatters in the composable
//
// API client: dashboard/api/fangate.js (Sanctum bearer auth via Rails proxy).
// Demo mock data when no token is configured; live fetch when connected.
// Variant tracking notes:
//   Fangate's POST /products/{id}/price-links CLONES the product (returns a
//   NEW product id with disjoint media-row ids — but the underlying S3 file
//   is shared, so `media[].source`'s path portion is stable across clones).
//   mergeVariants() unions cloned products into one card via union-find over
//   two signals: shared media paths + a localStorage variant-link map that
//   we record at clone-create time. Two channels = belt-and-suspenders;
//   either alone catches most cases, together they catch ~all.
// =============================================================================

import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import FangateAPI from 'dashboard/api/fangate';
import useFangateMoney from './composables/useFangateMoney';
import { groupProductsByMedia } from './composables/useFangateVariants';
import { useFangateLiveFilter } from './composables/useFangateLiveFilter';
import { useFangateCreatorNetwork } from './composables/useFangateCreatorNetwork';
import FangateConnectBanner from './components/FangateConnectBanner.vue';
import FangatePanelHeader from './components/FangatePanelHeader.vue';
import FangateLinkMenu from './components/FangateLinkMenu.vue';
import FangateProductCard from './components/FangateProductCard.vue';
import FangateContentToolbar from './components/FangateContentToolbar.vue';
import FangatePriceModal from './components/FangatePriceModal.vue';
import FangateUploadModal from './components/FangateUploadModal.vue';
import FangateItemEditModal from './components/FangateItemEditModal.vue';

const props = defineProps({
  activeInbox:    { type: Object, default: null },
  conversationId: { type: [Number, String], default: null },
});

const emit = defineEmits(['insert-into-chat']);
const store = useStore();

// =============================================================================
// State
// =============================================================================
// Bind the Fangate API client to whatever inbox is currently active so
// `getToken()` reads `fangate_token_<inboxId>` from localStorage.
FangateAPI.setActiveInbox(props.activeInbox?.id || null);

const isConnected = ref(FangateAPI.isConnected());
// Demo-mode guard: with no Fangate vault linked the panel shows MOCK_PRODUCTS,
// so every product/upload action is inert. Calling this pops a transient toast
// instead of pretending to act. Returns true when the action should be blocked.
const demoGuard = () => {
  if (isConnected.value) return false;
  useAlert('This is a demo — please connect a Fangate account via Settings → Chat-Accounts.');
  return true;
};
const creator     = ref(null);
const fetchError  = ref('');

// Creator currency (EUR/USD/GBP/…). Fangate's /api/products + /api/wallet
// return amounts in minor units WITHOUT a currency field — currency lives on
// the creator account and surfaces in /api/dashboard/summary's accounts[]
// (it's also on /api/user, but that endpoint is hard-blocked for API keys).
// Default to EUR until the summary loads.
const currency = ref('EUR');

const search         = ref('');
const selectedFolder = ref(null);
// Tab choices persist across navigation (the panel re-mounts on chat/account
// switch, so plain refs would reset to default each time).
const readTab = (key, fallback, allowed) => {
  try { const v = localStorage.getItem(key); return allowed.includes(v) ? v : fallback; }
  catch (_) { return fallback; }
};
// Top-level panel tab: 'content' = the vault content panel (default);
// 'sales' = purchase notifications from Fangate webhooks (Live Sales).
const panelTab = ref(readTab('fangate_panel_tab', 'content', ['content', 'sales']));
// Within Content: 'library' = browse/copy existing content (default);
// 'create-bundle' = build a new sellable bundle.
const vaultTab = ref(readTab('fangate_vault_tab', 'library', ['library', 'create-bundle']));
watch(panelTab, v => { try { localStorage.setItem('fangate_panel_tab', v); } catch (_) { /* ignore */ } });
watch(vaultTab, v => { try { localStorage.setItem('fangate_vault_tab', v); } catch (_) { /* ignore */ } });

// ---- Sales tab scope: 'account' = ALL of this account's webhook transactions;
// 'chat' = only those attributed to THIS conversation (coming soon — blank). ----
const salesScope = ref('account');
const panelSales = ref([]);            // current Transactions page (server-paginated)
const pPage = ref(1);
const pMeta = ref({ page: 1, per_page: 50, total: 0, total_pages: 1, has_more: false });
const pBuyerRows = ref([]);            // current Buyers page (server-grouped)
const pBuyerTxns = reactive({});       // buyer email -> [txns], lazy-loaded on expand
const salesLoading = ref(false);

// ---- "This Chat" scope: sales whose buyer_email is linked (FangateChatLink) to
// the open conversation's Telegram contact. Own pagination (20/page) + own sort
// (newest/highest/lowest) + own earnings tile; shares the period filter. ----
const ctSales = ref([]);
const ctPage = ref(1);
const ctMeta = ref({ page: 1, per_page: 20, total: 0, total_pages: 1, has_more: false });
const ctSummary = ref({ earnings: 0, currency: 'EUR' });
const ctSort = ref('new');             // new | price_high | price_low
const ctSortOpen = ref(false);
const ctLoading = ref(false);
const CT_SORTS = [['new', 'Newest'], ['price_high', 'Highest'], ['price_low', 'Lowest']];
const fpEur = n => '€' + Number(n || 0).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fpSaleDate = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};
const fpSaleStatus = s => {
  const e = s.event || '';
  if (e.indexOf('pending') >= 0) return { c: 'pending', l: 'Pending' };
  if (e.indexOf('fail') >= 0) return { c: 'failed', l: 'Failed' };
  return { c: 'paid', l: 'Paid' };
};
// Display amount = the buyer's END PRICE (gross). Prefer the EXACT set_price when
// Fangate sent it (include_set_price); otherwise fall back to reconstructing it
// from the net seller earning ((earning + 0.50) / 0.875, rounded) — flagged "est.".
const fpHasSetPrice = s => s.set_price !== null && s.set_price !== undefined && s.set_price !== '';
const fpSaleAmount = s => (fpHasSetPrice(s) ? Number(s.set_price) : Math.round((Number(s.seller_earning || 0) + 0.5) / 0.875));
const fpHasAmount = s => fpHasSetPrice(s) || fpSaleStatus(s).c === 'paid';
const fpIsEstimate = s => !fpHasSetPrice(s) && fpSaleStatus(s).c === 'paid'; // reconstruction used → show "est."
const fpAmtTitle = s => {
  if (fpHasSetPrice(s)) return 'End price (gross) — exact, from Fangate';
  if (fpSaleStatus(s).c === 'paid') return 'Estimated end price (gross) — seller earning ' + fpEur(Number(s.seller_earning || 0));
  return 'End price unavailable — set_price not delivered by the webhook';
};
// Server-paginated (50/page): the controller does period/status/sort/buyers, the
// panel just renders the page. references pRange()/pSort (declared below) only at
// CALL time — never during setup (the watch is non-immediate; initial load fires
// from onMounted), so there's no temporal-dead-zone hazard.
const loadPanelSales = async (resetPage = true) => {
  const id = props.activeInbox?.id;
  if (!id) { panelSales.value = []; pBuyerRows.value = []; return; }
  if (resetPage) pPage.value = 1;
  salesLoading.value = true;
  try {
    const r = pRange();
    const common = {
      from: r.from ? r.from.toISOString() : null,
      to: r.to ? r.to.toISOString() : null,
      page: pPage.value, perPage: 50,
    };
    if (pSort.value === 'buyers') {
      const d = (await FangateAPI.getSales(id, { ...common, view: 'buyers' })) || {};
      pBuyerRows.value = d.buyers || [];
      pMeta.value = d.meta || pMeta.value;
    } else {
      const d = (await FangateAPI.getSales(id, { ...common, view: 'tx', status: 'all', sort: pSort.value })) || {};
      panelSales.value = d.sales || [];
      pMeta.value = d.meta || pMeta.value;
    }
  } catch (_) { panelSales.value = []; pBuyerRows.value = []; }
  salesLoading.value = false;
};
// "This Chat" loader — server view=this_chat (20/page); shares the period filter.
const loadChatSales = async (reset = true) => {
  const id = props.activeInbox?.id;
  const conv = props.conversationId;
  if (!id || !conv) { ctSales.value = []; ctSummary.value = { earnings: 0, currency: 'EUR' }; return; }
  if (reset) ctPage.value = 1;
  ctLoading.value = true;
  try {
    const r = pRange();
    const d = (await FangateAPI.getSales(id, {
      from: r.from ? r.from.toISOString() : null,
      to: r.to ? r.to.toISOString() : null,
      view: 'this_chat', conversationId: conv, sort: ctSort.value,
      page: ctPage.value, perPage: 20,
    })) || {};
    ctSales.value = d.sales || [];
    ctMeta.value = d.meta || ctMeta.value;
    ctSummary.value = d.summary || { earnings: 0, currency: 'EUR' };
  } catch (_) { ctSales.value = []; }
  ctLoading.value = false;
};
// Reload whichever sales scope is active (the period filter is shared).
const pReload = (reset = true) => {
  if (salesScope.value === 'chat') loadChatSales(reset);
  else loadPanelSales(reset);
};
watch(
  [panelTab, salesScope, () => props.activeInbox?.id, () => props.conversationId],
  () => { if (panelTab.value === 'sales') pReload(true); }
);

// Live-refresh the active sales list when a sale lands for this inbox (the
// realtime pop-up itself is handled in FangateChatArea).
const onPanelSale = data => {
  if (Number(data?.inbox_id) === Number(props.activeInbox?.id) && panelTab.value === 'sales') {
    pReload(true);
  }
};

// ---- "Assign to Chat": link a sale's buyer email to a Telegram chat ---------
const assignMenu = reactive({ open: false, x: 0, y: 0, sale: null });
const assignModal = reactive({ open: false, email: '', search: '', convId: null, busy: false, done: false, error: '' });
function openAssignMenu(e, s) {
  const r = e.currentTarget.getBoundingClientRect();
  assignMenu.x = Math.min(r.left - 120, window.innerWidth - 170);
  assignMenu.y = r.bottom + 4;
  assignMenu.sale = s;
  assignMenu.open = true;
}
function openAssignModal() {
  const s = assignMenu.sale;
  assignMenu.open = false;
  if (!s) return;
  Object.assign(assignModal, { open: true, email: s.buyer_email || '', search: '', convId: null, busy: false, done: false, error: '' });
}
const assignChats = computed(() => {
  const inbox = Number(props.activeInbox?.id);
  const q = assignModal.search.trim().toLowerCase();
  return (store.getters.getAllConversations || [])
    .filter(c => Number(c.inbox_id) === inbox)
    .map(c => ({ id: c.id, name: c.meta?.sender?.name || ('Chat #' + c.id), avatar: c.meta?.sender?.thumbnail || '' }))
    .filter(c => !q || c.name.toLowerCase().includes(q))
    .slice(0, 60);
});
async function doAssign() {
  if (!assignModal.convId || assignModal.busy) return;
  assignModal.busy = true; assignModal.error = '';
  try {
    await FangateAPI.assignChatLink(props.activeInbox?.id, { buyerEmail: assignModal.email, conversationId: assignModal.convId });
    assignModal.done = true;
    useAlert('Email assigned to this chat — its sales now show under “This Chat”.');
    if (salesScope.value === 'chat') loadChatSales(true);
    setTimeout(() => { assignModal.open = false; }, 1000);
  } catch (_) {
    assignModal.error = 'Could not assign — please try again.';
  }
  assignModal.busy = false;
}
// Opened from the in-chat sale pop-up ("Assign this buyer to a Chat", emitted by
// FangateChatArea). Only act when the panel is on the sale's inbox.
const onAssignBuyerRequest = payload => {
  const email = payload?.email;
  if (!email) return;
  if (payload.inbox_id != null && Number(payload.inbox_id) !== Number(props.activeInbox?.id)) return;
  Object.assign(assignModal, { open: true, email, search: '', convId: null, busy: false, done: false, error: '' });
};

// ---- Account view: period filter + sort + Buyers grouping (compact port of the
// big FangateTransactions panel; all derived client-side over panelSales — no
// re-fetch). "Buyers" is the 4th sort option → switches to a grouped view. ----
const P_DAY = 86400000;
const pPeriod = ref('alltime');     // today|yesterday|thisweek|lastweek|thismonth|alltime|custom
const pCustom = ref(null);          // [fromDate, toDate]
const pSort = ref('new');           // new|price_high|price_low|buyers
const pPeriodOpen = ref(false);
const pCalOpen = ref(false);
const pSortOpen = ref(false);
const pExpanded = reactive({});     // buyer email -> bool
const P_PERIODS = [['today', 'Today'], ['yesterday', 'Yesterday'], ['thisweek', 'This week'], ['lastweek', 'Last week'], ['thismonth', 'This month'], ['alltime', 'All time'], ['custom', 'Custom…']];
const P_SORTS = [['new', 'Newest'], ['price_high', 'Price (highest)'], ['price_low', 'Price (lowest)'], ['buyers', 'Buyers']];
function pStartOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function pStartOfToday() { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }
function pStartOfWeek(d) { const x = new Date(d); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); x.setHours(0, 0, 0, 0); return x; }
const pFmtD = d => (d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '');
const pPriceEst = fpSaleAmount; // same (estimated) gross end price used for display + price sort
const pCalView = ref(pStartOfMonth(new Date()));
const pSelStart = ref(null);
const pSelEnd = ref(null);
function pRange() {
  const today = pStartOfToday();
  const end = new Date(today.getTime() + P_DAY - 1);
  switch (pPeriod.value) {
    case 'today': return { from: today, to: end };
    case 'yesterday': return { from: new Date(today.getTime() - P_DAY), to: new Date(today.getTime() - 1) };
    case 'thisweek': return { from: pStartOfWeek(today), to: end };
    case 'lastweek': return { from: new Date(pStartOfWeek(today).getTime() - 7 * P_DAY), to: new Date(pStartOfWeek(today).getTime() - 1) };
    case 'thismonth': return { from: pStartOfMonth(today), to: end };
    case 'custom': return pCustom.value ? { from: pCustom.value[0], to: pCustom.value[1] } : { from: null, to: null };
    default: return { from: null, to: null }; // alltime
  }
}
const pPeriodLabel = computed(() => {
  if (pPeriod.value === 'custom' && pCustom.value) return pFmtD(pCustom.value[0]) + ' – ' + pFmtD(pCustom.value[1]);
  return (P_PERIODS.find(p => p[0] === pPeriod.value) || P_PERIODS[5])[1];
});
const pSortLabel = computed(() => (P_SORTS.find(s => s[0] === pSort.value) || P_SORTS[0])[1]);
const isBuyers = computed(() => pSort.value === 'buyers');
// Server already did period-filter + sort + pagination — render the page as-is.
const pTxSorted = computed(() => panelSales.value);
// Buyers page (server-grouped). `total` is the seller-earning sum (server), like
// the Reports Buyers view; per-buyer txns are lazy-loaded on expand.
const pBuyers = computed(() => pBuyerRows.value.map(b => ({
  email: b.email, count: b.count, total: Number(b.earn || 0), products: b.products, txns: pBuyerTxns[b.email] || [],
})));
const pEmpty = computed(() => (isBuyers.value ? pBuyerRows.value.length === 0 : panelSales.value.length === 0));
const pTotalPages = computed(() => Math.max(1, pMeta.value.total_pages || 1));
function pGoPage(n) {
  if (n < 1 || n > pTotalPages.value || n === (pMeta.value.page || 1)) return;
  pPage.value = n;
  loadPanelSales(false);
}
// ---- This Chat: derived + sort + pagination --------------------------------
const ctEmpty = computed(() => ctSales.value.length === 0);
const ctTotalPages = computed(() => Math.max(1, ctMeta.value.total_pages || 1));
const ctSortLabel = computed(() => (CT_SORTS.find(s => s[0] === ctSort.value) || CT_SORTS[0])[1]);
const ctEarnings = computed(() => Number(ctSummary.value.earnings || 0));
function ctToggleSort() { const o = ctSortOpen.value; pClosePops(); ctSortOpen.value = !o; }
function ctSelectSort(s) { ctSort.value = s; ctSortOpen.value = false; loadChatSales(true); }
function ctGoPage(n) {
  if (n < 1 || n > ctTotalPages.value || n === (ctMeta.value.page || 1)) return;
  ctPage.value = n;
  loadChatSales(false);
}
// calendar (custom range)
const pCalCells = computed(() => {
  const y = pCalView.value.getFullYear(); const m = pCalView.value.getMonth();
  const startDay = (new Date(y, m, 1).getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  const today = pStartOfToday();
  const cells = [];
  for (let i = 0; i < startDay; i += 1) cells.push({ blank: true, key: 'b' + i });
  for (let d = 1; d <= days; d += 1) {
    const date = new Date(y, m, d);
    const inRange = pSelStart.value && pSelEnd.value && date >= pSelStart.value && date <= pSelEnd.value;
    const edge = (pSelStart.value && +date === +pSelStart.value) || (pSelEnd.value && +date === +pSelEnd.value);
    cells.push({ d, date, key: 'd' + d, muted: date > today, rng: !!inRange, edge: !!edge });
  }
  return cells;
});
const pCalMonth = computed(() => pCalView.value.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }));
const pCalHint = computed(() => (pSelStart.value ? (pFmtD(pSelStart.value) + (pSelEnd.value ? ' – ' + pFmtD(pSelEnd.value) : ' – …')) : 'Pick start date'));
function pCalPrev() { pCalView.value = new Date(pCalView.value.getFullYear(), pCalView.value.getMonth() - 1, 1); }
function pCalNext() { pCalView.value = new Date(pCalView.value.getFullYear(), pCalView.value.getMonth() + 1, 1); }
function pPickDay(c) {
  if (c.blank || c.muted) return;
  if (!pSelStart.value || (pSelStart.value && pSelEnd.value)) { pSelStart.value = c.date; pSelEnd.value = null; }
  else if (c.date < pSelStart.value) { pSelStart.value = c.date; }
  else { pSelEnd.value = c.date; }
}
function pApplyCustom() {
  if (!pSelStart.value || !pSelEnd.value) return;
  pCustom.value = [pSelStart.value, new Date(pSelEnd.value.getTime() + P_DAY - 1)];
  pPeriod.value = 'custom';
  pClosePops();
  pReload(true);
}
function pClosePops() { pPeriodOpen.value = false; pCalOpen.value = false; pSortOpen.value = false; ctSortOpen.value = false; }
function pTogglePeriod() { const o = pPeriodOpen.value || pCalOpen.value; pClosePops(); if (!o) pPeriodOpen.value = true; }
function pSelectPeriod(p) { if (p === 'custom') { pPeriodOpen.value = false; pCalOpen.value = true; return; } pPeriod.value = p; pCustom.value = null; pClosePops(); pReload(true); }
function pToggleSort() { const o = pSortOpen.value; pClosePops(); pSortOpen.value = !o; }
function pSelectSort(s) { pSort.value = s; pClosePops(); loadPanelSales(true); }
async function pToggleExpand(email) {
  pExpanded[email] = !pExpanded[email];
  const id = props.activeInbox?.id;
  if (pExpanded[email] && !pBuyerTxns[email] && id) {
    const r = pRange();
    const d = (await FangateAPI.getSales(id, {
      from: r.from ? r.from.toISOString() : null, to: r.to ? r.to.toISOString() : null,
      view: 'tx', status: 'successful', search: email, page: 1, perPage: 100,
    })) || {};
    pBuyerTxns[email] = d.sales || [];
  }
}
// Price filter (free/paid) was removed by request — only `type` left.
const activeFilters  = reactive({ type: new Set() });

// Sort mode for the product list. Default: newest first (#87). Sort happens
// client-side so we don't depend on Fangate's `sort_by` param values being
// stable — the underlying list still comes from listProducts() unsorted.
const sortMode = ref('newest');   // 'newest' | 'oldest' | 'title-asc' | 'price-desc' | 'price-asc'

const products  = ref([]);
const isLoadingProducts = ref(false);

// Product title for a sale row: prefer the name resolved+stored server-side,
// else fall back to the already-loaded products list (covers rows stored before
// server-side resolution existed), else "#id".
const pNameMap = computed(() => {
  const m = {};
  (products.value || []).forEach(p => { if (p && p.id != null) m[String(p.id)] = p.title || p.name || ''; });
  return m;
});
const pProdName = s => s.product_name || pNameMap.value[String(s.product_id)] || ('#' + s.product_id);

// Per-product link variants (multiple sales links per product).
// Structure: { [productId]: [ { id, title, price, url }, ... ] }
// activeLinkIdx: { [productId]: number }  → which variant is currently shown
const productLinks  = reactive({});
const activeLinkIdx = reactive({});
const linkSelectorFor = ref(null);   // productId of currently-open selector

// =============================================================================
// Mock data (until a Sanctum token is configured)
// =============================================================================
// Mock data — types use Fangate's API convention ('image' / 'video') so the
// toolbar's type filter matches mock and real data uniformly.
const MOCK_PRODUCTS = [
  { id: 'p1', title: 'Welcome Bundle',    price: 9.99,  type: 'image', items: 12, thumb: '', folder: 'Public'  },
  { id: 'p2', title: 'Premium Set',       price: 29.00, type: 'video', items: null, thumb: '', folder: 'Premium' },
  { id: 'p3', title: 'Behind the Scenes', price: 14.50, type: 'video', items: 4,   thumb: '', folder: 'Premium' },
  { id: 'p4', title: 'Free Sample',       price: 0,     type: 'image', items: 3,   thumb: '', folder: 'Public'  },
];
// Seed default link entry for a product if missing.
//
// Each entry now carries the REAL Fangate product id (when connected) — this
// is required because PATCH /products/{id}/price needs the variant's product
// id, and POST /products/{id}/price-links must target the source variant.
// For mock products we reuse the fake id so the editor still works in demo.
const ensureLink = product => {
  if (!productLinks[product.id]) {
    productLinks[product.id] = [{
      id:    product.id,                                 // ← real Fangate id
      title: product.title,
      price: product.price,
      url:   product.link || FangateAPI.buildProductLink(product.id),
    }];
    activeLinkIdx[product.id] = 0;
  }
};

// ----- Variant-link map (localStorage) -------------------------------------
// Fangate's POST /price-links creates a NEW PRODUCT (clone) and doesn't store
// any parent→child relationship. So we keep that mapping ourselves, scoped
// per Chatwoot inbox (same scoping rule as the Fangate token).
//
//   key   = `fangate_variant_links_<inboxId>`
//   value = { [sourceProductId]: [variantProductId, ...] }
//
// Recorded when the user creates a new link via the price modal. Feeds into
// mergeVariants() alongside media-path overlap (same union-find).
const variantMapKey = id => `fangate_variant_links_${id}`;
const loadVariantMap = () => {
  const id = FangateAPI.getActiveInbox();
  if (!id) return {};
  try { return JSON.parse(localStorage.getItem(variantMapKey(id)) || '{}'); }
  catch (_) { return {}; }
};
const saveVariantMap = m => {
  const id = FangateAPI.getActiveInbox();
  if (!id) return;
  try { localStorage.setItem(variantMapKey(id), JSON.stringify(m)); }
  catch (_) {}
};
const recordVariantLink = (sourceId, variantId) => {
  if (!sourceId || !variantId) return;
  const m = loadVariantMap();
  const list = m[sourceId] || [];
  if (!list.includes(variantId)) list.push(variantId);
  m[sourceId] = list;
  saveVariantMap(m);
};

// =============================================================================
// Loaders
// =============================================================================
// Fangate stores prices as integer cents (docs example: `"price": 4400` = €44.00).
// Normalize to euros at the API boundary so formatMoney / link.price stay
// decimal-friendly. When we wire PATCH /products/{id}/price later, convert
// back with Math.round(price * 100).
const centsToEuro = n => (typeof n === 'number' ? n / 100 : n);
const normalizeProduct = p => ({
  ...p,
  price: centsToEuro(p.price),
  total_earnings: centsToEuro(p.total_earnings),
});

const loadProducts = async ({ force = false } = {}) => {
  isLoadingProducts.value = true; fetchError.value = '';
  try {
    if (isConnected.value) {
      // force:true → refresh:true → X-Fangate-Refresh:1, which busts the proxy's
      // 120s Redis cache so a Reload picks up a just-changed moderation/consent
      // status immediately (matches FangateVaultView's force-refresh). A plain
      // loadProducts() keeps using the cache (instant tab-switch / account-switch).
      const resp = await FangateAPI.listProducts({ page: 1, limit: 50, refresh: force });
      // FangateAPI.listProducts already unwraps the outer `{ data: ... }`,
      // so `resp` is `{ pages_total, collection_link, data: [...products] }`.
      // Defensive fallbacks for older mocked shapes too.
      const list = resp?.data ?? resp?.payload ?? resp ?? [];
      products.value = Array.isArray(list) ? list.map(normalizeProduct) : [];
    } else {
      products.value = MOCK_PRODUCTS;
    }

    // Re-merge cloned variants under one product card.
    // Without this, every clone would render as its own standalone card —
    // confusing for the user since they think of them as variants of one item.
    mergeVariants();
    products.value.forEach(ensureLink);
  } catch (e) {
    fetchError.value = e?.message || 'Failed to load products';
    products.value = MOCK_PRODUCTS;   // graceful fallback
  } finally {
    isLoadingProducts.value = false;
  }
};

// Collapse cloned products into one card per cluster using the shared
// useFangateVariants grouping (union-find over media-path + localStorage map).
// Non-primary cluster members get pushed into productLinks[primary.id] as
// L2/L3/… variants and removed from the top-level products list.
const mergeVariants = () => {
  const items = products.value || [];
  if (items.length < 2) return;

  const clusters = groupProductsByMedia(items, { variantMap: loadVariantMap() });
  const primaries = [];

  clusters.forEach(({ primary, variants }) => {
    primaries.push(primary);
    if (variants.length === 1) return;   // singleton — nothing to absorb
    ensureLink(primary);
    variants.slice(1).forEach(variant => {
      const already = productLinks[primary.id].some(l => String(l.id) === String(variant.id));
      if (already) return;
      productLinks[primary.id].push({
        id:    variant.id,
        title: variant.title,
        price: variant.price,
        url:   variant.link || FangateAPI.buildProductLink(variant.id),
      });
    });
  });

  products.value = primaries;
};

// Pull the creator's currency_code from /api/dashboard/summary. We try a
// few likely locations because the docs only say it lives "in accounts[]"
// without nailing the exact shape — defensive fallbacks keep this from
// silently breaking if Fangate restructures the payload.
const loadCurrency = async () => {
  if (!isConnected.value) { currency.value = 'EUR'; return; }
  try {
    const summary = await FangateAPI.getDashboardSummary();
    const code =
      summary?.accounts?.[0]?.currency_code ||
      summary?.currency_code ||
      summary?.creator?.currency_code ||
      'EUR';
    currency.value = String(code).toUpperCase();
  } catch (_) {
    currency.value = 'EUR';   // silent fallback — currency is best-effort
  }
};

// Note: Fangate's /api/user is gated to dashboard-session users — generated
// API keys get a 403 ("Account settings are not available for generated API
// keys."), so we don't try to fetch the creator profile here. The "Vault from
// {Creator}" pill falls back to the inbox name, which is correct for the
// chat-account-scoped model.
const loadProfile = async () => { creator.value = null; };

// =============================================================================
// Filters + Sort
// =============================================================================
// TYPE_OPTIONS and SORT_OPTIONS lists live inside FangateContentToolbar.
// The panel only owns the user's current selection (search / folder / sort /
// activeFilters.type) and the derived filtered+sorted product list below.

const toggleFilter = (group, value) => {
  const set = activeFilters[group];
  if (set.has(value)) set.delete(value);
  else set.add(value);
};
const clearFilters = () => {
  activeFilters.type.clear();
  selectedFolder.value = null;
};

// Live-only toggle + lazy consent cache, shared with the Vault Library via
// the same composable so cache hits across both views.
const inboxIdRef = computed(() => props.activeInbox?.id || null);
const {
  liveOnly,
  enqueueConsentFetches,
  isLive,
} = useFangateLiveFilter({
  inboxIdRef,
  liveOnlyStorageKey: 'fangate_panel_live_only',
});

const filteredProducts = computed(() => {
  let list = products.value || [];
  if (selectedFolder.value) list = list.filter(p => p.folder === selectedFolder.value);
  if (activeFilters.type.size) list = list.filter(p => activeFilters.type.has(p.type));
  if (search.value) {
    const q = search.value.toLowerCase();
    list = list.filter(p => (p.title || '').toLowerCase().includes(q));
  }
  // Live-only: keep items that are confirmed live OR still loading consent
  // data (so we don't drop items the user is about to see flip to live).
  if (liveOnly.value) {
    list = list.filter(p => isLive(p) !== false);
  }
  return list;
});

const folders = computed(() => {
  const set = new Set();
  (products.value || []).forEach(p => { if (p.folder) set.add(p.folder); });
  return [...set];
});
const filterCount = computed(() => activeFilters.type.size);

// Id of the bundle created moments ago → pins it to the top + drives the brief
// color flash on its card. Cleared automatically after a few seconds.
const newProductId = ref(null);
let newProductTimer = null;
onUnmounted(() => clearTimeout(newProductTimer));

// Client-side sort applied on top of filteredProducts. Reads from a few likely
// timestamp fields (Fangate may return Unix seconds, ISO strings, or only id);
// falls back to numeric id descending which works for sequential ids.
const sortedProducts = computed(() => {
  const list = [...filteredProducts.value];
  const ts = p => {
    const raw = p.created_at ?? p.created_at_timestamp ?? p.createdAt ?? p.id ?? 0;
    // ISO string → epoch ms; numeric → as-is.
    if (typeof raw === 'string' && /\D/.test(raw)) {
      const ms = Date.parse(raw);
      return Number.isFinite(ms) ? ms : 0;
    }
    return Number(raw) || 0;
  };
  let out;
  switch (sortMode.value) {
    case 'oldest':     out = list.sort((a, b) => ts(a) - ts(b)); break;
    case 'title-asc':  out = list.sort((a, b) => (a.title || '').localeCompare(b.title || '')); break;
    case 'price-asc':  out = list.sort((a, b) => (a.price || 0) - (b.price || 0)); break;
    case 'price-desc': out = list.sort((a, b) => (b.price || 0) - (a.price || 0)); break;
    case 'newest':
    default:           out = list.sort((a, b) => ts(b) - ts(a));
  }
  // A just-created bundle is pinned to the very top while its highlight runs,
  // regardless of the active sort — so the user always SEES what they created.
  if (newProductId.value != null) {
    const idx = out.findIndex(p => p.id === newProductId.value);
    if (idx > 0) {
      const [p] = out.splice(idx, 1);
      out.unshift(p);
    }
  }
  return out;
});

// Paginate at 10 per page — keeps the panel snappy (max 10 lazy consent
// fetches per page navigation) and matches the user's expectation that
// the side-panel always shows the newest 10 first. Reset to page 1
// whenever filters or sort change.
const PANEL_PER_PAGE = 10;
const currentPage = ref(1);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(sortedProducts.value.length / PANEL_PER_PAGE)));
const pagedProducts = computed(() => {
  const start = (currentPage.value - 1) * PANEL_PER_PAGE;
  return sortedProducts.value.slice(start, start + PANEL_PER_PAGE);
});
watch([search, selectedFolder, sortMode, liveOnly,
       () => activeFilters.type.size],
      () => { currentPage.value = 1; });
watch(totalPages, n => { if (currentPage.value > n) currentPage.value = n; });
const goToPage = n => {
  const target = Math.min(Math.max(1, n), totalPages.value);
  if (target !== currentPage.value) currentPage.value = target;
};
const prevPage = () => goToPage(currentPage.value - 1);
const nextPage = () => goToPage(currentPage.value + 1);

// Compact page list — always show: 1, current-1, current, current+1, last;
// fill gaps with '…'. Keeps the pager narrow enough to fit the side-panel
// even when the total page count grows.
const pageNumbers = computed(() => {
  const total = totalPages.value;
  const cur   = currentPage.value;
  const want  = new Set([1, total, cur, cur - 1, cur + 1]);
  const nums  = [...want].filter(n => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < nums.length; i++) {
    out.push(nums[i]);
    if (i < nums.length - 1 && nums[i + 1] > nums[i] + 1) out.push('…');
  }
  return out;
});

// Trigger lazy consent-tag fetches whenever the VISIBLE slice changes —
// only the current page, never the whole list. enqueueConsentFetches is
// idempotent + concurrency-capped so calling on every recompute is safe.
watch(() => pagedProducts.value, list => enqueueConsentFetches(list), { immediate: true });

// Active link for a product (resolved from variants)
const activeLinkOf = product => {
  ensureLink(product);
  const arr = productLinks[product.id] || [];
  return arr[activeLinkIdx[product.id] || 0] || arr[0];
};

// =============================================================================
// Card actions
// =============================================================================
const copyToChat = product => {
  if (demoGuard()) return;
  const link = activeLinkOf(product);
  if (link?.url) emit('insert-into-chat', link.url);
};

const copiedId = ref(null);
const copyLinkWithFeedback = async product => {
  if (demoGuard()) return;
  const link = activeLinkOf(product);
  if (!link?.url) return;
  try { await navigator.clipboard.writeText(link.url); }
  catch (_) {
    const ta = document.createElement('textarea');
    ta.value = link.url;
    ta.style.cssText = 'position:fixed;top:-9999px;';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch (_) {}
    document.body.removeChild(ta);
  }
  copiedId.value = product.id;
  setTimeout(() => { if (copiedId.value === product.id) copiedId.value = null; }, 1800);
};

// Link selector dropdown (per product card, position:fixed)
const linkSelectorPos = ref({ left: 0, top: 0 });
const openLinkSelector = (e, productId) => {
  e.stopPropagation();
  const rect = e.currentTarget.getBoundingClientRect();
  linkSelectorPos.value = { left: rect.left - 100, top: rect.bottom + 4 };
  linkSelectorFor.value = linkSelectorFor.value === productId ? null : productId;
};
const selectLink = (productId, idx) => {
  activeLinkIdx[productId] = idx;
  linkSelectorFor.value    = null;
};

// =============================================================================
// EDIT-PRICE MODAL — thin host
// =============================================================================
// The modal itself (FangatePriceModal.vue) owns the form, the validation,
// and the API calls. The panel only:
//   • supplies the product + active variant to edit
//   • absorbs the saved-* events into productLinks / variant-map
//   • opens / closes via priceModalOpen
const priceModalOpen           = ref(false);
const priceModalProduct        = ref(null);
const priceModalActiveVariant  = ref(null);

const openPriceModal = product => {
  if (demoGuard()) return;
  ensureLink(product);
  const idx = activeLinkIdx[product.id] || 0;
  priceModalProduct.value       = product;
  priceModalActiveVariant.value = productLinks[product.id][idx];
  priceModalOpen.value          = true;
};

// 'change' result: merge updated price/url back into the same variant slot.
const onPriceChanged = updated => {
  const product = priceModalProduct.value;
  if (!product) return;
  const idx = activeLinkIdx[product.id] || 0;
  const arr = productLinks[product.id] || [];
  if (!arr[idx]) return;
  arr[idx] = { ...arr[idx], ...updated };
};

// 'new' result: append the new variant under the same card, mark it active,
// and record the source→variant link so reloads keep them grouped.
// ===========================================================================
// EDIT-ITEM MODAL — opens the full Vault-detail-style editor for one card.
// ===========================================================================
const itemEditOpen     = ref(false);
const itemEditProduct  = ref(null);

const openItemEditModal = product => {
  if (demoGuard()) return;
  ensureLink(product);
  const idx = activeLinkIdx[product.id] || 0;
  const variant = productLinks[product.id][idx];
  // The variant carries the editable fields (price, link). Merge with the
  // product so the modal sees the full Fangate payload.
  itemEditProduct.value = { ...product, ...variant };
  itemEditOpen.value = true;
};

// Save handler — modal already PATCHed Fangate; reflect the merged product
// into our local state. If the saved product is the cluster primary, also
// update the variant copy so the card shows the new title/price/link.
const onItemEdited = updated => {
  if (!updated?.id) return;
  // Update top-level products list
  const pi = products.value.findIndex(p => p.id === updated.id);
  if (pi > -1) {
    const next = [...products.value];
    next[pi] = { ...next[pi], ...updated };
    products.value = next;
  }
  // Update active variant in productLinks
  const links = productLinks[updated.id];
  if (Array.isArray(links)) {
    const li = links.findIndex(l => l.id === updated.id);
    if (li > -1) {
      links[li] = { ...links[li],
                    title: updated.title ?? links[li].title,
                    price: updated.price ?? links[li].price,
                    url:   updated.link  ?? links[li].url };
    }
  }
};

// Edit-Item modal's "Edit price" chip → route through the existing price
// modal flow (which we already have wired). Keep both modals open: user
// clicks the chip, price modal opens on top, change/new-link works, close
// price modal lands back in the item editor.
const onItemEditPriceRequest = () => {
  if (!itemEditProduct.value) return;
  // Reuse openPriceModal — it already resolves the active variant.
  openPriceModal(itemEditProduct.value);
};

// Card → "Link" chip: open the ACTIVE variant's checkout URL in a new tab.
// Multi-variant products have a different URL per variant; we honor the
// one the user currently has selected via the L1/L2 picker.
const openLinkFor = product => {
  if (demoGuard()) return;
  const link = activeLinkOf(product);
  const url = link?.url || product?.link || product?.url;
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
};

// Card → "Content" chip: open the underlying content file in a new tab.
// Falls through media[0].source → media[0].preview_url → media[0].url →
// product preview/thumb so we always have something to show even when
// Fangate's response shape varies.
const openContentFor = product => {
  if (demoGuard()) return;
  if (!product) return;
  const media = Array.isArray(product.media) ? product.media : [];
  const first = media[0] || {};
  const url = first.source
           || first.preview_url
           || first.url
           || product.preview
           || product.thumb
           || '';
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
};

// Active variant for the currently-open Edit-Item modal — passed into the
// modal so its "Preview link" button respects the selected variant.
const itemEditActiveLink = computed(() => {
  const p = itemEditProduct.value;
  if (!p) return null;
  const links = productLinks[p.id];
  if (!Array.isArray(links) || !links.length) return null;
  const idx = activeLinkIdx[p.id] || 0;
  return links[idx] || links[0] || null;
});

const onNewLinkCreated = (sourceVariantId, newVariant) => {
  const product = priceModalProduct.value;
  if (!product) return;
  ensureLink(product);
  const newIdx = productLinks[product.id].length;
  productLinks[product.id].push(newVariant);
  activeLinkIdx[product.id] = newIdx;
  if (sourceVariantId && newVariant?.id) {
    recordVariantLink(sourceVariantId, newVariant.id);
  }
};

// =============================================================================
// UPLOAD MODAL — thin host
// =============================================================================
// FangateUploadModal owns the form, file input, validation, payout calc, and
// the (currently simulated) save flow. Panel just opens it and absorbs the
// resulting product into the list optimistically.
const uploadModalOpen = ref(false);
// =============================================================================
// Create Bundle — pick existing media → POST /products {media_ids, price, …}.
// A "bundle" is just a Fangate product referencing several media rows.
// =============================================================================
const bundleMedia         = ref([]);
const bundleMediaLoading  = ref(false);
const bundleMediaError    = ref('');
const bundleMediaPage     = ref(1);
const bundleMediaLastPage = ref(1);
// #37 media-level consent: media_id -> worst co-creator consent status across the
// products it sits in. Fangate has no per-media is_accessible (product-level only)
// and no media-consent endpoint, but each consent-creator-tag now carries media_id —
// so we fetch tags per product (cached) and index them by media. Empty = not required.
const mediaConsent        = reactive({});
const mediaConsentDone     = new Set(); // product ids already fetched (per inbox)
const bundleSelected      = reactive(new Set());
const bundleModalOpen     = ref(false);
// downloadable default ON. Yoti age-check (is_verif_age) + co-creator tagging
// are opt-in (default OFF). is_adult_content is kept fixed-true in the API call
// (sensible default for creator content; no UI toggle).
const bundleForm = reactive({
  title: '', description: '', priceEur: '',
  downloadable: true, verifyAge: false, tagCoCreator: false,
});
const bundleCreating = ref(false);
const bundleError    = ref('');

// ---- Co-creator tagging (mirrors FangateUploadModal) -----------------------
// When "Tag a co-creator" is on, the bundle needs the tagged creators' consent
// (is_should_consent). The product doesn't exist until create, so we collect
// the picks here and fire addProductConsentTag once we have the product id.
const coTagSearch = reactive({ query: '', results: [], loading: false, error: '' });
const coTagsToAdd = ref([]);   // [{ creatorId, label }]
let coTagsSearchTimer = null;
const onCoTagInput = () => {
  if (coTagsSearchTimer) clearTimeout(coTagsSearchTimer);
  coTagsSearchTimer = setTimeout(runCoTagSearch, 220);
};
const runCoTagSearch = async () => {
  const q = String(coTagSearch.query || '').trim();
  if (q.length < 2) { coTagSearch.results = []; coTagSearch.error = ''; return; }
  coTagSearch.loading = true; coTagSearch.error = '';
  try {
    const r = await FangateAPI.searchCreators(q);
    coTagSearch.results = Array.isArray(r) ? r : [];
  } catch (e) {
    coTagSearch.results = []; coTagSearch.error = e?.message || 'Search failed';
  } finally {
    coTagSearch.loading = false;
  }
};
const creatorLabelOf = c => {
  if (!c) return 'Unknown';
  if (c.display_name) return c.display_name;
  const full = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return full || c.email || `Creator #${c.id ?? '?'}`;
};
const addCoTagFromCreator = c => {
  if (!c?.id) return;
  if (coTagsToAdd.value.some(t => t.creatorId === c.id)) return;
  coTagsToAdd.value = [...coTagsToAdd.value, { creatorId: c.id, label: creatorLabelOf(c) }];
  coTagSearch.query = ''; coTagSearch.results = [];
};
const removeCoTag = idx => { coTagsToAdd.value = coTagsToAdd.value.filter((_, i) => i !== idx); };

// Quick-select: co-creators who previously accepted a tag (re-taggable without
// a new approval). Loaded lazily/cached when the tag toggle turns on.
const { networkState: coNetworkState, loadNetwork: loadCoNetwork } = useFangateCreatorNetwork();
watch(() => bundleForm.tagCoCreator, on => { if (on) loadCoNetwork(); });
const coTagQuickPicks = computed(() => {
  const taggedIds = new Set(coTagsToAdd.value.map(t => t.creatorId));
  return (coNetworkState().creators || []).filter(c => !taggedIds.has(c.id)).slice(0, 12);
});
// Post-create "not live yet / in review" notice popup.
const reviewNotice   = reactive({ open: false, containsVideo: false });

// Step 1: pick items → "Create bundle" opens the settings popup.
const openBundleModal = () => {
  if (demoGuard()) return;
  if (!bundleSelected.size) return;
  bundleError.value = '';
  bundleModalOpen.value = true;
};
const closeBundleModal = () => { if (!bundleCreating.value) bundleModalOpen.value = false; };

const loadBundleMedia = async ({ append = false, refresh = false } = {}) => {
  if (!isConnected.value) return;   // demo mode — template shows the hint
  bundleMediaLoading.value = true;
  bundleMediaError.value = '';
  try {
    // 'approved' = the moderation cleared this media. The media endpoint carries no
    // consent flag, so we filter moderation here (server + client guard) and layer
    // media-level CONSENT on top via refreshBundleConsent() (#37) — consent-creator
    // tags now carry media_id, so a media tagged with a co-creator who hasn't
    // accepted is hidden from the picker.
    const body = await FangateAPI.listMedia({
      page: bundleMediaPage.value,
      limit: 20,
      status: 'approved',
      refresh,
    });
    // Real response shape (verified against fangate.info 2026-06-12):
    //   { success, errors_message, data: { current_page, pages_total, per_page,
    //     total, data: [ {id, type, status, preview, created_at, …} ] } }
    const payload = body?.data && !Array.isArray(body.data) ? body.data : body;
    const items = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
    bundleMedia.value = append ? [...bundleMedia.value, ...items] : items;
    bundleMediaLastPage.value = Number(payload?.pages_total || payload?.last_page || 1);
    refreshBundleConsent(refresh); // #37: layer media-level consent over the moderation filter (background)
  } catch (e) {
    bundleMediaError.value = e?.message || 'Failed to load media.';
  } finally {
    bundleMediaLoading.value = false;
  }
};
const bundleHasMoreMedia = computed(() => bundleMediaPage.value < bundleMediaLastPage.value);
const loadMoreBundleMedia = () => { bundleMediaPage.value += 1; loadBundleMedia({ append: true }); };

// #37: pull co-creator consent tags for the products the loaded bundle media sit in,
// and index by media_id (declined > pending > accepted wins). Cached per product;
// runs in the background so the grid paints fast, then re-filters as consent resolves.
const CONSENT_RANK = { accepted: 1, pending: 2, declined: 3 };
const normConsent = s => {
  const v = String(s || 'pending').toLowerCase();
  if (v.startsWith('accept')) return 'accepted';
  if (v.startsWith('declin') || v.startsWith('reject') || v.startsWith('rescind')
   || v.startsWith('withdraw') || v.startsWith('revoke')) return 'declined';
  return 'pending';
};
const refreshBundleConsent = async (refresh = false) => {
  if (refresh) mediaConsentDone.clear();
  const todo = [];
  for (const m of bundleMedia.value) {
    for (const pid of (m.product_ids || [])) {
      if (!mediaConsentDone.has(pid)) { mediaConsentDone.add(pid); todo.push(pid); }
    }
  }
  if (!todo.length) return;
  let idx = 0;
  const worker = async () => {
    while (idx < todo.length) {
      const pid = todo[idx++];
      try {
        const tags = await FangateAPI.listProductConsentTags(pid, { refresh });
        for (const t of (Array.isArray(tags) ? tags : [])) {
          const mid = t?.media_id;
          if (mid == null) continue;
          const next = normConsent(t.status || t.decision);
          const cur = mediaConsent[mid];
          if (!cur || CONSENT_RANK[next] > CONSENT_RANK[cur]) mediaConsent[mid] = next;
        }
      } catch (_) { /* leave undefined → media stays shown (no resolvable consent) */ }
    }
  };
  await Promise.all(Array.from({ length: Math.min(4, todo.length) }, () => worker()));
};

// Lazy-load when the tab opens.
watch(vaultTab, v => {
  if (v === 'create-bundle' && !bundleMedia.value.length) { bundleMediaPage.value = 1; loadBundleMedia(); }
});

// The SAME toolbar filters as the Library apply here, client-side: folder,
// type (Photos/Video), search and sort. Price sorts don't apply to media
// (no price) — they fall back to newest-first.
const displayedBundleMedia = computed(() => {
  // #37: a media is pickable only if moderation cleared it (approved) AND its
  // media-level co-creator consent is satisfied — provided (accepted) or not
  // required (no tag). Pending/declined drop out once their tags resolve.
  let list = bundleMedia.value.filter(m =>
    m.status === 'approved'
    && mediaConsent[m.id] !== 'pending'
    && mediaConsent[m.id] !== 'declined');
  if (selectedFolder.value) {
    list = list.filter(m => m.folder === selectedFolder.value || m?.folder?.name === selectedFolder.value);
  }
  if (activeFilters.type.size) list = list.filter(m => activeFilters.type.has(m.type));
  if (search.value) {
    const q = search.value.toLowerCase();
    list = list.filter(m =>
      (m.title || '').toLowerCase().includes(q) ||
      (m.description || '').toLowerCase().includes(q));
  }
  const ts = m => Date.parse(m?.created_at || '') || Number(m?.id) || 0;
  switch (sortMode.value) {
    case 'oldest':    return list.sort((a, b) => ts(a) - ts(b));
    case 'title-asc': return list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    default:          return list.sort((a, b) => ts(b) - ts(a)); // newest first
  }
});

// Toolbar reload: force-refresh whatever the active sub-view shows. force:true
// busts the proxy's 120s Redis cache so a just-changed moderation/consent status
// shows immediately instead of only after the TTL expires (was the panel bug:
// products reload did nothing because it never sent X-Fangate-Refresh).
const onToolbarRefresh = () => {
  loadProducts({ force: true });
  if (vaultTab.value === 'create-bundle') {
    bundleMediaPage.value = 1;
    loadBundleMedia({ refresh: true });
  }
};

const toggleBundleMedia = m => {
  if (m?.status === 'rejected') return;   // rejected media can't be sold
  if (bundleSelected.has(m.id)) bundleSelected.delete(m.id);
  else bundleSelected.add(m.id);
};

// Price entered in € (comma or dot) → integer cents. Fangate minimum = 500.
const bundlePriceCents = computed(() => {
  const v = parseFloat(String(bundleForm.priceEur).replace(',', '.'));
  return Number.isFinite(v) ? Math.round(v * 100) : NaN;
});
// Either the title OR the public description must carry at least 20 characters
// (UI rule only — not enforced by our server).
const bundleTextOk = computed(() =>
  bundleForm.title.trim().length >= 20 || bundleForm.description.trim().length >= 20
);
const canCreateBundle = computed(() =>
  bundleSelected.size > 0 &&
  bundleTextOk.value &&
  Number.isFinite(bundlePriceCents.value) && bundlePriceCents.value >= 500 &&
  !bundleCreating.value
);

const createBundle = async () => {
  if (demoGuard() || !canCreateBundle.value) return;
  bundleCreating.value = true;
  bundleError.value = '';
  // Capture which selected media are videos BEFORE the selection is cleared —
  // drives the "video can take longer to review" emphasis in the notice.
  const containsVideo = bundleMedia.value.some(m => bundleSelected.has(m.id) && m.type === 'video');
  try {
    const product = await FangateAPI.createBundle({
      mediaIds: [...bundleSelected],
      price: bundlePriceCents.value,
      title: bundleForm.title.trim(),
      publicDescription: bundleForm.description.trim(),
      isDownloadable: bundleForm.downloadable,
      isVerifyAge: bundleForm.verifyAge,
      // Tagging a co-creator means the bundle needs their consent.
      isShouldConsent: bundleForm.tagCoCreator,
    });
    // Fire the collected co-creator consent tags now that the product exists.
    // Per-tag failures are non-fatal (the product is already created) — they're
    // surfaced as a soft note. Mirrors FangateUploadModal.
    if (bundleForm.tagCoCreator && product?.id && coTagsToAdd.value.length) {
      const today = new Date().toISOString().slice(0, 10);
      const tagErrors = [];
      for (const t of coTagsToAdd.value) {
        try {
          await FangateAPI.addProductConsentTag({ productId: product.id, date: today, taggedCreatorId: t.creatorId });
        } catch (e) {
          tagErrors.push(`${t.label}: ${e?.message || 'failed'}`);
        }
      }
      if (tagErrors.length) useAlert(`Bundle created, but some co-creator tags failed: ${tagErrors.join('; ')}`);
    }
    bundleModalOpen.value = false;
    bundleSelected.clear();
    bundleForm.title = ''; bundleForm.description = ''; bundleForm.priceEur = '';
    bundleForm.tagCoCreator = false;
    coTagsToAdd.value = []; coTagSearch.query = ''; coTagSearch.results = [];
    // Drop the sales link straight into the active chat's composer.
    if (product) copyToChat(product);
    vaultTab.value = 'library';        // back to Library…
    currentPage.value = 1;             // …on page 1, where the new bundle sits
    await loadProducts();              // …with the fresh bundle in the grid
    // Pin the new bundle to the top + flash its card for a few seconds.
    newProductId.value = product?.id ?? null;
    clearTimeout(newProductTimer);
    newProductTimer = setTimeout(() => { newProductId.value = null; }, 5000);
    // A freshly created bundle goes into Fangate moderation — it is not live
    // until approved. If it isn't already live, pop a notice so the agent knows
    // not to expect it to sell yet (video review in particular can take longer).
    const fresh = (products.value || []).find(p => p.id === product?.id) || product;
    if (isLive(fresh) !== true) {
      reviewNotice.containsVideo = containsVideo;
      reviewNotice.open = true;
    } else {
      useAlert('Bundle created — link inserted into the chat input.');
    }
  } catch (e) {
    bundleError.value = e?.message || 'Failed to create bundle.';
  } finally {
    bundleCreating.value = false;
  }
};

const openUploadModal = () => { if (demoGuard()) return; uploadModalOpen.value = true; };

// Insert the freshly-built product at the top of the list and seed a default
// link entry so the L-pill machinery treats it like any other card.
const onUploaded = newProduct => {
  products.value = [newProduct, ...products.value];
  ensureLink(newProduct);
};

// =============================================================================
// Lifecycle + outside-click
// =============================================================================
// Only the product-card link-variant menu lives at panel scope. Toolbar
// dropdowns close themselves via their own outside-click listener.
const closeLinkSelector = () => { linkSelectorFor.value = null; pClosePops(); };
onMounted(() => {
  lastLoadedInboxId = props.activeInbox?.id || null;
  loadProfile();
  loadCurrency();
  loadProducts();
  document.addEventListener('click', closeLinkSelector);
  emitter.on('fangate:sale', onPanelSale);
  emitter.on('fangate:assign-buyer', onAssignBuyerRequest);
  // Initial Sales load — here (not an immediate watch) so pRange()/pSort are
  // already initialised when loadPanelSales runs.
  if (panelTab.value === 'sales' && salesScope.value === 'account') loadPanelSales(true);
});
onUnmounted(() => {
  document.removeEventListener('click', closeLinkSelector);
  emitter.off('fangate:sale', onPanelSale);
  emitter.off('fangate:assign-buyer', onAssignBuyerRequest);
  clearTimeout(revalidateTimer);
});

// When the active chat-account changes, switch token scope and reload.
// Guards:
//   - Skip transient null/undefined transitions (navigating to Settings can
//     briefly null out selectedInbox; we don't want that to clear state).
//   - Skip no-op repeats when the same id resolves twice (e.g. after a
//     v-show toggle round-trip).
// ---- Per-inbox snapshot cache (stale-while-revalidate) ----------------------
// Switching chat-accounts should feel instant: show the target vault's
// previously-loaded items immediately and refresh in the background. The cache
// is keyed by inbox id, so it NEVER shows another vault's data (the bug this
// also guards against). Lives in-memory for the panel's lifetime; a snapshot
// younger than REVALIDATE_AFTER skips the background refresh entirely so rapid
// back-and-forth clicking does no network at all.
const REVALIDATE_AFTER = 30_000;
const panelCache = new Map();   // inboxId -> { products, bundleMedia, …, ts }
let revalidateTimer = null;

const nowMs = () => new Date().getTime();

const snapshotPanel = id => {
  if (!id) return;
  panelCache.set(id, {
    products: products.value,
    bundleMedia: bundleMedia.value,
    bundleMediaLastPage: bundleMediaLastPage.value,
    creator: creator.value,
    currency: currency.value,
    ts: nowMs(),
  });
};

const hydratePanel = id => {
  const c = panelCache.get(id);
  if (!c) return false;
  products.value            = c.products;
  products.value.forEach(ensureLink);   // idempotent — rebuild L-pill state
  bundleMedia.value         = c.bundleMedia;
  bundleMediaLastPage.value = c.bundleMediaLastPage;
  creator.value             = c.creator;
  currency.value            = c.currency;
  return true;
};

// Background refresh for the currently-active inbox, then re-snapshot.
const revalidatePanel = async () => {
  await Promise.all([loadProfile(), loadCurrency(), loadProducts()]);
  if (vaultTab.value === 'create-bundle' && isConnected.value) {
    bundleMediaPage.value = 1;
    await loadBundleMedia();
  }
  snapshotPanel(lastLoadedInboxId);
};
const scheduleRevalidate = () => {
  clearTimeout(revalidateTimer);
  revalidateTimer = setTimeout(revalidatePanel, 350);   // debounce rapid switches
};

let lastLoadedInboxId = null;
watch(() => props.activeInbox?.id, (id, oldId) => {
  if (!id || id === lastLoadedInboxId) return;
  snapshotPanel(oldId ?? lastLoadedInboxId);   // remember what we're leaving
  lastLoadedInboxId = id;
  FangateAPI.setActiveInbox(id);
  isConnected.value = FangateAPI.isConnected();

  if (panelCache.has(id)) {
    // Snappy: show the cached vault immediately…
    hydratePanel(id);
    // …and refresh in the background unless it was just loaded.
    if (nowMs() - panelCache.get(id).ts > REVALIDATE_AFTER) scheduleRevalidate();
  } else {
    // No snapshot for this inbox → reset (never show the previous vault) + load.
    creator.value = null;
    resetBundleState();
    loadProfile();
    loadCurrency();
    loadProducts();
    if (vaultTab.value === 'create-bundle' && isConnected.value) {
      bundleMediaPage.value = 1;
      loadBundleMedia();
    }
  }
});

// Clear all per-vault Create-Bundle state so a chat-account switch never shows
// the previous vault's media/selection/form.
const resetBundleState = () => {
  bundleMedia.value = [];
  bundleSelected.clear();
  bundleMediaPage.value = 1;
  bundleMediaLastPage.value = 1;
  bundleMediaError.value = '';
  Object.keys(mediaConsent).forEach(k => delete mediaConsent[k]); // #37: per-inbox consent
  mediaConsentDone.clear();
  bundleError.value = '';
  bundleModalOpen.value = false;
  newProductId.value = null;
  bundleForm.title = '';
  bundleForm.description = '';
  bundleForm.priceEur = '';
  bundleForm.downloadable = true;
  bundleForm.verifyAge = false;
  bundleForm.tagCoCreator = false;
  coTagsToAdd.value = [];
  coTagSearch.query = '';
  coTagSearch.results = [];
  coTagSearch.error = '';
};

// =============================================================================
// Helpers — money formatters from the shared composable.
// Reactive: when currency.value changes, every formatted string flows through.
// (Connection / API-token entry lives in Settings → Chat-Accounts → Edit.
//  This panel only reads `isConnected` from the FangateAPI client.)
// =============================================================================
const { formatMoney, formatBalance, currencySymbol } = useFangateMoney(currency);
</script>

<template>
  <div class="fp-root">

    <!-- ========== Header ========== -->
    <FangatePanelHeader
      :creator-label="creator?.name?.first || activeInbox?.name || 'Creator'"
      :is-connected="isConnected"
      @open-upload="openUploadModal"
    />

    <!-- ========== Top-level tabs: Content (vault) | Sales (webhooks) ========== -->
    <div class="fp-tabs">
      <button class="fp-tab" :class="{ active: panelTab === 'content' }"
              @click="panelTab = 'content'">Content</button>
      <button class="fp-tab" :class="{ active: panelTab === 'sales' }"
              @click="panelTab = 'sales'">Sales</button>
    </div>

    <!-- ========== Demo banner (or "live" pill when connected) ========== -->
    <FangateConnectBanner
      :is-connected="isConnected"
      :creator-label="creator?.name?.first || creator?.email || 'Fangate'"
    />

    <!-- ========== CONTENT TAB — the vault content panel ========== -->
    <template v-if="panelTab === 'content'">
    <FangateContentToolbar
      v-model:search="search"
      v-model:folder="selectedFolder"
      v-model:sort="sortMode"
      :active-types="[...activeFilters.type]"
      :folders="folders"
      :filter-count="filterCount"
      :is-loading-products="isLoadingProducts"
      @toggle-type="toggleFilter('type', $event)"
      @clear-filters="clearFilters"
      @refresh="onToolbarRefresh"
    />

    <!-- "Live only" toggle pill + top page navigation. Toggle persists per
         inbox in localStorage. Pager only renders when there are more than
         one page of results. -->
    <div class="fp-live-toggle-row" :class="{ 'fp-live-toggle-row--solo': vaultTab !== 'library' }">
      <!-- Library only: filters products to those with a live payment link.
           Hidden in Create Bundle — there the media grid is ALWAYS limited to
           live (approved) media, so the toggle would be a no-op. -->
      <button v-if="vaultTab === 'library'" class="fp-live-toggle"
              :class="{ active: liveOnly }"
              @click="liveOnly = !liveOnly"
              :title="liveOnly
                      ? 'Showing only live items (approved + consent OK). Click to show all.'
                      : 'Showing all items. Non-live are dimmed. Click to filter.'">
        <span class="fp-live-toggle-dot" :class="{ on: liveOnly }"></span>
        Live links only
      </button>
      <!-- Vault mode switch — Library (browse/copy) vs Create Bundle (Sales). -->
      <div class="fp-mode-toggle">
        <button class="fp-mode-btn" :class="{ active: vaultTab === 'library' }"
                @click="vaultTab = 'library'">Library</button>
        <button class="fp-mode-btn" :class="{ active: vaultTab === 'create-bundle' }"
                @click="vaultTab = 'create-bundle'">Create Bundle</button>
      </div>
      <div v-if="totalPages > 1 && vaultTab === 'library'" class="fp-pager">
        <button class="fp-pager-btn" :disabled="currentPage <= 1" @click="prevPage" title="Previous page">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <template v-for="(item, i) in pageNumbers" :key="i">
          <span v-if="item === '…'" class="fp-pager-gap">…</span>
          <button v-else
                  class="fp-pager-num"
                  :class="{ active: item === currentPage }"
                  @click="goToPage(item)">{{ item }}</button>
        </template>
        <button class="fp-pager-btn" :disabled="currentPage >= totalPages" @click="nextPage" title="Next page">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Body: product cards -->
    <div class="fp-body">
      <!-- Create Bundle mode — step 1: pick media (the toolbar's folder/search/
           type/sort filters above apply here too); step 2: "Create bundle"
           opens the settings popup. -->
      <div v-if="vaultTab === 'create-bundle'" class="fp-bundle">
        <div v-if="!isConnected" class="fp-empty">Connect a Fangate vault (Settings → Chat-Accounts) to build bundles.</div>
        <div v-else-if="bundleMediaError" class="fp-error">{{ bundleMediaError }}</div>
        <div v-else-if="bundleMediaLoading && !bundleMedia.length" class="fp-empty">Loading media…</div>
        <div v-else-if="!displayedBundleMedia.length" class="fp-empty">
          {{ bundleMedia.length ? 'No media match the current filters.' : 'No live media available.' }}
        </div>

        <div v-if="isConnected && displayedBundleMedia.length" class="fp-bundle-grid">
          <button v-for="m in displayedBundleMedia" :key="'bm-' + m.id" type="button"
                  class="fp-bundle-item"
                  :class="{ selected: bundleSelected.has(m.id) }"
                  :title="m.title || ('#' + m.id)"
                  @click="toggleBundleMedia(m)">
            <img v-if="m.preview || m.preview_blurred" :src="m.preview || m.preview_blurred"
                 :alt="m.title || ('media ' + m.id)" loading="lazy" />
            <span v-else class="fp-bundle-item-type">{{ m.type || 'file' }}</span>
            <span v-if="bundleSelected.has(m.id)" class="fp-bundle-check">✓</span>
            <a v-if="m.source" class="fp-bundle-deeplink" :href="m.source"
               target="_blank" rel="noopener noreferrer"
               title="Open source file in a new tab" @click.stop>
              <svg viewBox="0 0 24 24" width="11" height="11" fill="none"
                   stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 4h6v6"/><path d="M20 4l-9 9"/>
                <path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6"/>
              </svg>
            </a>
          </button>
        </div>
        <button v-if="bundleHasMoreMedia" class="fp-bundle-more"
                :disabled="bundleMediaLoading" @click="loadMoreBundleMedia">
          {{ bundleMediaLoading ? 'Loading…' : 'Load more media' }}
        </button>

        <!-- Sticky action bar: selection count + the step-2 trigger -->
        <div v-if="isConnected" class="fp-bundle-bar">
          <span class="fp-bundle-count">{{ bundleSelected.size }} selected</span>
          <button class="fp-bundle-create" :disabled="!bundleSelected.size" @click="openBundleModal">
            Create bundle
          </button>
        </div>
      </div>
      <template v-else>
      <!-- Only blank for "Loading…" when there's nothing cached to show, so a
           background revalidate after a snappy account switch doesn't flicker. -->
      <div v-if="isLoadingProducts && !products.length" class="fp-empty">Loading…</div>
      <div v-else-if="fetchError"   class="fp-error">{{ fetchError }}</div>
      <div v-else-if="!sortedProducts.length" class="fp-empty">
        {{ liveOnly ? 'No live items match.' : 'No content found.' }}
      </div>
      <FangateProductCard
        v-for="item in pagedProducts"
        :key="item.id"
        :class="{ 'fp-card-just-created': item.id === newProductId }"
        :product="item"
        :active-link="activeLinkOf(item)"
        :variant-count="(productLinks[item.id] || []).length"
        :active-idx="activeLinkIdx[item.id] || 0"
        :is-copied="copiedId === item.id"
        :format-money="formatMoney"
        :is-live="isLive(item) !== false"
        @copy-to-chat="copyToChat(item)"
        @copy-link="copyLinkWithFeedback(item)"
        @edit-price="openPriceModal(item)"
        @edit-item="openItemEditModal(item)"
        @open-link="openLinkFor(item)"
        @open-content="openContentFor(item)"
        @open-link-selector="e => openLinkSelector(e, item.id)"
      />
      <!-- Bottom pager — only when paginating and there's content on this
           page. Same controls as the top row so the user doesn't scroll up. -->
      <div v-if="totalPages > 1 && pagedProducts.length" class="fp-pager fp-pager--bottom">
        <button class="fp-pager-btn" :disabled="currentPage <= 1" @click="prevPage" title="Previous page">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <template v-for="(item, i) in pageNumbers" :key="i">
          <span v-if="item === '…'" class="fp-pager-gap">…</span>
          <button v-else
                  class="fp-pager-num"
                  :class="{ active: item === currentPage }"
                  @click="goToPage(item)">{{ item }}</button>
        </template>
        <button class="fp-pager-btn" :disabled="currentPage >= totalPages" @click="nextPage" title="Next page">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>
      </template>
    </div>
    </template>

    <!-- ========== SALES TAB — Account (all webhook txns) | This Chat (soon) ========== -->
    <template v-else>
      <div class="fp-tabs fp-tabs--scope">
        <button class="fp-tab" :class="{ active: salesScope === 'account' }" @click="salesScope = 'account'">Account</button>
        <button class="fp-tab" :class="{ active: salesScope === 'chat' }" @click="salesScope = 'chat'">This Chat</button>
      </div>
      <template v-if="salesScope === 'account'">
        <!-- Controls: period (left) + sort incl. Buyers (right) -->
        <div class="fp-sctl">
          <div class="fp-period">
            <button class="fp-pbtn" @click.stop="pTogglePeriod">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <span>{{ pPeriodLabel }}</span>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </button>
            <div v-if="pPeriodOpen" class="fp-pop fp-menu" @click.stop>
              <div v-for="p in P_PERIODS" :key="p[0]" class="fp-mi" :class="{ cur: pPeriod === p[0] }" @click="pSelectPeriod(p[0])">{{ p[1] }}</div>
            </div>
            <div v-if="pCalOpen" class="fp-pop fp-cal" @click.stop>
              <div class="fp-calhead">
                <button class="fp-navb" aria-label="Previous month" @click="pCalPrev"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg></button>
                <span class="fp-calm">{{ pCalMonth }}</span>
                <button class="fp-navb" aria-label="Next month" @click="pCalNext"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></button>
              </div>
              <div class="fp-dow"><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span><span>Su</span></div>
              <div class="fp-cgrid">
                <div v-for="c in pCalCells" :key="c.key" class="fp-cd" :class="{ blank: c.blank, mut: c.muted, rng: c.rng, edge: c.edge }" @click="pPickDay(c)">{{ c.d }}</div>
              </div>
              <div class="fp-calfoot"><span>{{ pCalHint }}</span><button class="fp-apply" :disabled="!pSelStart || !pSelEnd" @click="pApplyCustom">Apply</button></div>
            </div>
          </div>
          <div class="fp-sortw">
            <button class="fp-sbtn" @click.stop="pToggleSort"><span class="mut">Sort:</span><span>{{ pSortLabel }}</span><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg></button>
            <div v-if="pSortOpen" class="fp-pop fp-menu fp-menu--r" @click.stop>
              <div v-for="s in P_SORTS" :key="s[0]" class="fp-mi" :class="{ cur: pSort === s[0] }" @click="pSelectSort(s[0])">{{ s[1] }}</div>
            </div>
          </div>
        </div>

        <div v-if="salesLoading" class="fp-sales-placeholder"><div class="fp-bundle-ph-sub">Loading…</div></div>
        <div v-else-if="pEmpty" class="fp-sales-placeholder">
          <div class="fp-bundle-ph-title">{{ isBuyers ? 'No buyers' : 'No sales' }} in this period</div>
          <div class="fp-bundle-ph-sub">Account purchases (via the Fangate webhook) appear here.</div>
        </div>

        <!-- Buyers: grouped by email (alphabetical), expandable to the individual sales -->
        <div v-else-if="isBuyers" class="fp-sales-list">
          <div v-for="b in pBuyers" :key="b.email" class="fp-buyer">
            <button class="fp-buyer-row" @click="pToggleExpand(b.email)">
              <svg class="fp-bchev" :class="{ open: pExpanded[b.email] }" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
              <span class="fp-buyer-email">{{ b.email }}</span>
              <span class="fp-buyer-meta" :title="'Seller earning across ' + b.count + ' purchase(s)'">{{ b.count }}× · {{ fpEur(b.total) }}</span>
            </button>
            <div v-if="pExpanded[b.email]" class="fp-buyer-detail">
              <div v-for="s in b.txns" :key="s.id || s.transaction_id" class="fp-sale fp-sale--sub">
                <div class="fp-sale-row1">
                  <span class="fp-sale-prod">{{ pProdName(s) }}</span>
                  <span class="fp-sale-amt" :title="fpAmtTitle(s)">{{ fpHasAmount(s) ? fpEur(fpSaleAmount(s)) : '—' }}<span v-if="fpIsEstimate(s)" class="fp-sale-est">est.</span></span>
                </div>
                <div class="fp-sale-row2">
                  <span class="fp-sale-pill" :class="fpSaleStatus(s).c">{{ fpSaleStatus(s).l }}</span>
                  <span v-if="s.product_id" class="fp-sale-pid">#{{ s.product_id }}</span>
                  <span class="fp-sale-date">{{ fpSaleDate(s.occurred_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Transactions: flat list (period-filtered + sorted) -->
        <div v-else class="fp-sales-list">
          <div v-for="s in pTxSorted" :key="s.id || s.transaction_id" class="fp-sale">
            <div class="fp-sale-row1">
              <span class="fp-sale-email">{{ s.buyer_email || '—' }}</span>
              <span v-if="s.product_id" class="fp-sale-pid">#{{ s.product_id }}</span>
              <span class="fp-sale-amt" :title="fpAmtTitle(s)">{{ fpHasAmount(s) ? fpEur(fpSaleAmount(s)) : '—' }}<span v-if="fpIsEstimate(s)" class="fp-sale-est">est.</span></span>
              <button v-if="s.buyer_email" class="fp-sale-menu" type="button" title="Assign to chat" @click.stop="openAssignMenu($event, s)">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.7"/><circle cx="12" cy="12" r="1.7"/><circle cx="12" cy="19" r="1.7"/></svg>
              </button>
            </div>
            <div class="fp-sale-row2">
              <span class="fp-sale-pill" :class="fpSaleStatus(s).c">{{ fpSaleStatus(s).l }}</span>
              <span class="fp-sale-title">{{ pProdName(s) }}</span>
              <span class="fp-sale-date">{{ fpSaleDate(s.occurred_at) }}</span>
            </div>
          </div>
        </div>
        <div v-if="!salesLoading && pMeta.total > pMeta.per_page" class="fp-pager">
          <button class="fp-pgb" type="button" :disabled="(pMeta.page || 1) <= 1" @click="pGoPage((pMeta.page || 1) - 1)">‹ Prev</button>
          <span class="fp-pginfo">{{ pMeta.page || 1 }} / {{ pTotalPages }}</span>
          <button class="fp-pgb" type="button" :disabled="!pMeta.has_more" @click="pGoPage((pMeta.page || 1) + 1)">Next ›</button>
        </div>
      </template>
      <template v-else>
        <!-- No conversation open (e.g. fullscreen vault) → nothing to attribute to -->
        <div v-if="!conversationId" class="fp-sales-placeholder">
          <div class="fp-bundle-ph-title">This chat</div>
          <div class="fp-bundle-ph-sub">Open a conversation to see the sales attributed to it.</div>
        </div>
        <template v-else>
          <!-- Controls: period (shared with Account) + sort (newest/highest/lowest) -->
          <div class="fp-sctl">
            <div class="fp-period">
              <button class="fp-pbtn" @click.stop="pTogglePeriod">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                <span>{{ pPeriodLabel }}</span>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
              </button>
              <div v-if="pPeriodOpen" class="fp-pop fp-menu" @click.stop>
                <div v-for="p in P_PERIODS" :key="p[0]" class="fp-mi" :class="{ cur: pPeriod === p[0] }" @click="pSelectPeriod(p[0])">{{ p[1] }}</div>
              </div>
              <div v-if="pCalOpen" class="fp-pop fp-cal" @click.stop>
                <div class="fp-calhead">
                  <button class="fp-navb" aria-label="Previous month" @click="pCalPrev"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg></button>
                  <span class="fp-calm">{{ pCalMonth }}</span>
                  <button class="fp-navb" aria-label="Next month" @click="pCalNext"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg></button>
                </div>
                <div class="fp-dow"><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span><span>Su</span></div>
                <div class="fp-cgrid">
                  <div v-for="c in pCalCells" :key="c.key" class="fp-cd" :class="{ blank: c.blank, mut: c.muted, rng: c.rng, edge: c.edge }" @click="pPickDay(c)">{{ c.d }}</div>
                </div>
                <div class="fp-calfoot"><span>{{ pCalHint }}</span><button class="fp-apply" :disabled="!pSelStart || !pSelEnd" @click="pApplyCustom">Apply</button></div>
              </div>
            </div>
            <div class="fp-sortw">
              <button class="fp-sbtn" @click.stop="ctToggleSort"><span class="mut">Sort:</span><span>{{ ctSortLabel }}</span><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg></button>
              <div v-if="ctSortOpen" class="fp-pop fp-menu fp-menu--r" @click.stop>
                <div v-for="s in CT_SORTS" :key="s[0]" class="fp-mi" :class="{ cur: ctSort === s[0] }" @click="ctSelectSort(s[0])">{{ s[1] }}</div>
              </div>
            </div>
          </div>

          <!-- Accumulated seller earnings of the sales attributed to this chat -->
          <div class="fp-ct-earn">
            <span class="fp-ct-earn-lbl">Earnings from this Chat</span>
            <span class="fp-ct-earn-val">{{ fpEur(ctEarnings) }}</span>
          </div>

          <div v-if="ctLoading" class="fp-sales-placeholder"><div class="fp-bundle-ph-sub">Loading…</div></div>
          <div v-else-if="ctEmpty" class="fp-sales-placeholder">
            <div class="fp-bundle-ph-title">No sales for this chat</div>
            <div class="fp-bundle-ph-sub">Use ⋮ → “Assign to Chat” on a sale in Account to attribute a buyer’s email here.</div>
          </div>
          <div v-else class="fp-sales-list">
            <div v-for="s in ctSales" :key="s.id || s.transaction_id" class="fp-sale">
              <div class="fp-sale-row1">
                <span class="fp-sale-email">{{ s.buyer_email || '—' }}</span>
                <span v-if="s.product_id" class="fp-sale-pid">#{{ s.product_id }}</span>
                <span class="fp-sale-amt" :title="fpAmtTitle(s)">{{ fpHasAmount(s) ? fpEur(fpSaleAmount(s)) : '—' }}<span v-if="fpIsEstimate(s)" class="fp-sale-est">est.</span></span>
              </div>
              <div class="fp-sale-row2">
                <span class="fp-sale-pill" :class="fpSaleStatus(s).c">{{ fpSaleStatus(s).l }}</span>
                <span class="fp-sale-title">{{ pProdName(s) }}</span>
                <span class="fp-sale-date">{{ fpSaleDate(s.occurred_at) }}</span>
              </div>
            </div>
          </div>
          <div v-if="!ctLoading && ctMeta.total > ctMeta.per_page" class="fp-pager">
            <button class="fp-pgb" type="button" :disabled="(ctMeta.page || 1) <= 1" @click="ctGoPage((ctMeta.page || 1) - 1)">‹ Prev</button>
            <span class="fp-pginfo">{{ ctMeta.page || 1 }} / {{ ctTotalPages }}</span>
            <button class="fp-pgb" type="button" :disabled="!ctMeta.has_more" @click="ctGoPage((ctMeta.page || 1) + 1)">Next ›</button>
          </div>
        </template>
      </template>
    </template>

    <!-- ========== ASSIGN-TO-CHAT: ⋮ popover + modal ========== -->
    <div v-if="assignMenu.open" class="fp-am-backdrop" @click="assignMenu.open = false"></div>
    <div v-if="assignMenu.open" class="fp-am-menu" :style="{ left: assignMenu.x + 'px', top: assignMenu.y + 'px' }" @click.stop>
      <button class="fp-am-mi" type="button" @click="openAssignModal">Assign to Chat</button>
    </div>

    <div v-if="assignModal.open" class="fp-modal-backdrop" @click="assignModal.open = false">
      <div class="fp-modal" @click.stop>
        <div class="fp-modal-head">
          <span class="fp-modal-title">Assign email to a chat</span>
          <button class="fp-modal-x" type="button" aria-label="Close" @click="assignModal.open = false">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <p class="fp-modal-hint">
          Link this buyer’s email to a Telegram chat. From then on every sale with this email is
          also tracked under that chat’s <strong>“This Chat”</strong> tab — for individual per-chat sales stats.
        </p>
        <label class="fp-modal-lbl">Buyer email</label>
        <div class="fp-modal-email">{{ assignModal.email }}</div>
        <label class="fp-modal-lbl">Select Chat</label>
        <input v-model="assignModal.search" class="fp-modal-search" type="text" placeholder="Search chats of this account…" />
        <div class="fp-modal-chats">
          <div v-if="!assignChats.length" class="fp-modal-chats-empty">No chats found.</div>
          <button
            v-for="c in assignChats" :key="c.id" type="button"
            class="fp-modal-chat" :class="{ sel: assignModal.convId === c.id }"
            @click="assignModal.convId = c.id"
          >
            <img v-if="c.avatar" :src="c.avatar" class="fp-modal-chat-av" alt="" />
            <span v-else class="fp-modal-chat-av fp-modal-chat-av--ph">{{ (c.name || '?').slice(0, 1).toUpperCase() }}</span>
            <span class="fp-modal-chat-name">{{ c.name }}</span>
            <svg v-if="assignModal.convId === c.id" class="fp-modal-chat-tick" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
          </button>
        </div>
        <div v-if="assignModal.error" class="fp-modal-err">{{ assignModal.error }}</div>
        <div class="fp-modal-foot">
          <button class="fp-modal-cancel" type="button" @click="assignModal.open = false">Cancel</button>
          <button class="fp-modal-assign" type="button" :disabled="!assignModal.convId || assignModal.busy" @click="doAssign">
            {{ assignModal.done ? '✓ Assigned' : (assignModal.busy ? 'Assigning…' : 'Assign') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ========== LINK SELECTOR DROPDOWN (position:fixed) ========== -->
    <FangateLinkMenu
      v-if="linkSelectorFor"
      :links="productLinks[linkSelectorFor] || []"
      :active-idx="activeLinkIdx[linkSelectorFor] || 0"
      :position="linkSelectorPos"
      :format-money="formatMoney"
      @select="idx => selectLink(linkSelectorFor, idx)"
    />

    <!-- ========== EDIT-PRICE MODAL ========== -->
    <!-- ========== CREATE-BUNDLE SETTINGS POPUP (step 2) ========== -->
    <div v-if="bundleModalOpen" class="fp-bm-overlay" @click.self="closeBundleModal">
      <div class="fp-bm-modal">
        <div class="fp-bm-head">
          <h3 class="fp-bm-title">Create bundle</h3>
          <button class="fp-bm-close" title="Close" @click="closeBundleModal">×</button>
        </div>
        <div class="fp-bm-sub">{{ bundleSelected.size }} items selected</div>

        <input v-model="bundleForm.title" class="fp-bundle-input" placeholder="Title" />
        <textarea v-model="bundleForm.description" class="fp-bundle-input" rows="3"
                  placeholder="Public description"></textarea>
        <div class="fp-bundle-avs" :style="bundleTextOk ? '' : 'color:#b45309;font-weight:600;'">
          Title or description needs at least 20 characters.
        </div>
        <input v-model="bundleForm.priceEur" class="fp-bundle-input" inputmode="decimal"
               placeholder="Price in € (min 5.00)" />
        <label class="fp-bundle-flag">
          <input v-model="bundleForm.downloadable" type="checkbox" /> Downloadable
        </label>
        <label class="fp-bundle-flag">
          <input v-model="bundleForm.verifyAge" type="checkbox" />
          Require age-check via Yoti <span class="fp-flag-hint">(ID check from buyer)</span>
        </label>
        <label class="fp-bundle-flag">
          <input v-model="bundleForm.tagCoCreator" type="checkbox" />
          Tag a co-creator <span class="fp-flag-hint">(needs their consent)</span>
        </label>

        <!-- Co-creator tagging — only when the toggle is on. Same search +
             multi-tag UX as the content Upload modal; consent requests are sent
             after the bundle is created. -->
        <div v-if="bundleForm.tagCoCreator" class="fp-bundle-cotag">
          <div class="fp-bundle-cotag-note">
            Required when a third person is depicted — Fangate asks each tagged
            creator to confirm consent before the bundle can go live.
          </div>

          <div v-if="coTagsToAdd.length" class="fp-bundle-cotag-chips">
            <span v-for="(t, idx) in coTagsToAdd" :key="idx" class="fp-bundle-cotag-chip">
              {{ t.label }}
              <button class="fp-bundle-cotag-x" @click.stop="removeCoTag(idx)">×</button>
            </span>
          </div>

          <!-- Quick-select: previously-accepted co-creators (re-tag, no new approval) -->
          <div v-if="coNetworkState().loading" class="fp-bundle-cotag-hint">Loading your collaborators…</div>
          <div v-else-if="coTagQuickPicks.length" class="fp-bundle-cotag-quick">
            <span class="fp-bundle-cotag-quick-label">Previously accepted — tap to tag again:</span>
            <div class="fp-bundle-cotag-chips">
              <button v-for="c in coTagQuickPicks" :key="'qp-' + c.id" type="button"
                      class="fp-bundle-cotag-chip fp-bundle-cotag-chip--pick"
                      @click.stop="addCoTagFromCreator(c)">
                + {{ creatorLabelOf(c) }}
              </button>
            </div>
          </div>

          <input v-model="coTagSearch.query" @input="onCoTagInput"
                 class="fp-bundle-input" placeholder="Search creators by name or email…" />
          <div v-if="coTagSearch.loading" class="fp-bundle-cotag-hint">Searching…</div>
          <div v-else-if="coTagSearch.error" class="fp-error">{{ coTagSearch.error }}</div>
          <div v-else-if="coTagSearch.results.length" class="fp-bundle-cotag-results">
            <button v-for="c in coTagSearch.results" :key="c.id" type="button"
                    class="fp-bundle-cotag-result" @click.stop="addCoTagFromCreator(c)">
              <span class="fp-bundle-cotag-result-name">{{ creatorLabelOf(c) }}</span>
              <span v-if="c.email" class="fp-bundle-cotag-result-email">{{ c.email }}</span>
            </button>
          </div>
          <div v-else-if="coTagSearch.query.trim().length >= 2" class="fp-bundle-cotag-hint">
            No matches on Fangate for "{{ coTagSearch.query.trim() }}".
          </div>
        </div>

        <div v-if="bundleError" class="fp-error">{{ bundleError }}</div>
        <button class="fp-bundle-create" :disabled="!canCreateBundle" @click="createBundle">
          {{ bundleCreating ? 'Creating…' : 'Create product' }}
        </button>
      </div>
    </div>

    <!-- ===== POST-CREATE "in review" notice ===== -->
    <div v-if="reviewNotice.open" class="fp-bm-overlay" @click.self="reviewNotice.open = false">
      <div class="fp-bm-modal fp-review-modal">
        <div class="fp-bm-head">
          <h3 class="fp-bm-title">Link not live yet</h3>
          <button class="fp-bm-close" title="Close" @click="reviewNotice.open = false">×</button>
        </div>
        <p class="fp-review-text">
          Usually a co-creator’s consent is still missing — please obtain it.
          In some cases the content may not be approved yet<template v-if="reviewNotice.containsVideo"> (video can take longer)</template> —
          check in Fangate or your vault and re-create if needed.
        </p>
        <button class="fp-bundle-create" @click="reviewNotice.open = false">Got it</button>
      </div>
    </div>

    <FangatePriceModal
      v-model:open="priceModalOpen"
      :product="priceModalProduct"
      :active-variant="priceModalActiveVariant"
      :is-connected="isConnected"
      :format-balance="formatBalance"
      :currency-symbol="currencySymbol"
      @saved-change-price="onPriceChanged"
      @saved-new-link="onNewLinkCreated"
    />

    <!-- ========== UPLOAD MODAL ========== -->
    <FangateUploadModal
      v-model:open="uploadModalOpen"
      :vault-name="creator?.name?.first || activeInbox?.name || 'Creator'"
      :is-connected="isConnected"
      :format-balance="formatBalance"
      :currency-symbol="currencySymbol"
      @uploaded="onUploaded"
    />

    <!-- Edit-Item modal — opens from the pencil button on each product card.
         Full Vault-detail-style form. Save handler echoes the merged product
         back into the panel's grid so the card refreshes without a full
         reload. -->
    <FangateItemEditModal
      v-model:open="itemEditOpen"
      :product="itemEditProduct"
      :active-link="itemEditActiveLink"
      :is-connected="isConnected"
      :format-balance="formatBalance"
      :currency-symbol="currencySymbol"
      @saved="onItemEdited"
      @request-edit-price="onItemEditPriceRequest"
    />
  </div>
</template>

<style lang="scss" scoped>
// Most CSS moved to children — see Component tree in script's header comment.
// What's left here owns only the panel shell (the root flex column) and the
// scrollable product-list container. Loading / empty / error placeholders
// share the .fp-empty rule.
.fp-root {
  display: flex; flex-direction: column; height: 100%;
  background: var(--bg-app);
  overflow: hidden;
}
.fp-body {
  flex: 1; min-height: 0; overflow-y: auto;
  padding: 8px 12px;
  display: flex; flex-direction: column; gap: 5px;
}
.fp-empty, .fp-error {
  font-family: 'Inter', sans-serif;
  font-size: 12px; color: var(--ink-muted);
  text-align: center; padding: 24px 8px;
}
.fp-error { color: #b91c1c; }

// "Live only" toggle pill sits left, pager sits right. When there's only
// one page, the pager hides and the toggle becomes the only thing in the row.
.fp-live-toggle-row {
  padding: 6px 12px 0;
  display: flex; justify-content: space-between; align-items: center;
  gap: 6px; flex-wrap: nowrap;
}
// Create Bundle mode: the Live-links toggle is hidden, so keep the mode
// switch anchored right instead of jumping left.
.fp-live-toggle-row--solo { justify-content: flex-end; }
.fp-live-toggle {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px 4px 6px;
  white-space: nowrap; flex-shrink: 0;
  border-radius: 999px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 700;
  cursor: pointer;
  transition: all 0.12s;
  &:hover { border-color: #047857; color: #047857; }
  &.active {
    background: rgba(16, 185, 129, 0.10);
    border-color: #047857;
    color: #047857;
  }
}
// Library / Create Bundle segmented switch (Sales Live).
.fp-mode-toggle {
  display: inline-flex;
  flex-shrink: 0;
  border: 1px solid var(--border-soft);
  border-radius: 999px;
  overflow: hidden;
  background: var(--bg-panel);
}
.fp-mode-btn {
  padding: 4px 9px;
  border: none;
  background: transparent;
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.12s;
}
.fp-mode-btn:hover { color: #047857; }
.fp-mode-btn.active {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
}
// Top-level Content | Sales tabs.
.fp-tabs {
  display: flex;
  gap: 5px;
  padding: 8px 12px 2px;
}
.fp-tab {
  flex: 1;
  padding: 7px 10px;
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  background: var(--bg-panel);
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.12s;
}
.fp-tab:hover { color: #047857; border-color: #047857; }
.fp-tab.active {
  background: rgba(16, 185, 129, 0.12);
  border-color: #047857;
  color: #047857;
}
.fp-bundle-placeholder,
.fp-sales-placeholder {
  padding: 40px 16px;
  text-align: center;
}
.fp-tabs--scope { padding-top: 12px; padding-bottom: 6px; }
.fp-sales-list { display: flex; flex-direction: column; gap: 8px; padding: 2px 0 14px; }
.fp-sale {
  background: var(--bg-panel); border: 1px solid var(--border-soft); border-radius: 10px;
  padding: 9px 11px; box-shadow: 0 1px 2px rgba(31, 42, 42, 0.05);
}
.fp-sale-row1 { display: flex; align-items: center; gap: 7px; }
.fp-sale-prod {
  font: 500 13px 'Inter', sans-serif; color: var(--ink-strong);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.fp-sale-email {
  font: 600 12px 'Inter', sans-serif; color: var(--ink-strong);
  min-width: 0; white-space: normal; overflow-wrap: anywhere; line-height: 1.3;
}
.fp-sale-amt { font: 600 13px 'Inter', sans-serif; color: var(--ink-strong); flex-shrink: 0; margin-left: auto; font-variant-numeric: tabular-nums; }
.fp-sale-est { font-size: 8.5px; font-weight: 500; color: var(--ink-muted); margin-left: 3px; letter-spacing: .02em; }
.fp-sale-row2 { display: flex; align-items: center; gap: 6px; margin-top: 5px; font-size: 11.5px; color: var(--ink-muted); }
.fp-sale-pid { background: var(--bg-subtle); border-radius: 5px; padding: 1px 5px; font-size: 11px; font-variant-numeric: tabular-nums; color: var(--ink-muted); flex-shrink: 0; }
.fp-pager { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 10px 0 4px; }
.fp-pgb { border: 1px solid var(--border-soft); background: var(--bg-panel); color: var(--ink-medium); border-radius: 8px; padding: 5px 11px; font: 500 11.5px 'Inter', sans-serif; cursor: pointer; }
.fp-pgb:hover:not(:disabled) { border-color: var(--accent); color: var(--accent-strong); }
.fp-pgb:disabled { opacity: 0.45; cursor: default; }
.fp-pginfo { font: 500 11.5px 'Inter', sans-serif; color: var(--ink-muted); font-variant-numeric: tabular-nums; }
.fp-sale-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; color: var(--ink-medium); font-weight: 500; }
.fp-sale-date { flex-shrink: 0; }
.fp-sale-pill { padding: 1px 7px; border-radius: 999px; font-size: 10px; font-weight: 600; flex-shrink: 0; }
.fp-sale-pill.paid { background: rgba(16, 185, 129, 0.16); color: #047857; }
.fp-sale-pill.pending { background: #fbedd2; color: #92580b; }
.fp-sale-pill.failed { background: #fbe3e3; color: #b3261e; }

/* ⋮ assign-to-chat trigger on a sale card */
.fp-sale-menu { flex-shrink: 0; margin-left: 2px; border: none; background: none; padding: 2px; border-radius: 6px; color: var(--ink-muted); cursor: pointer; line-height: 0; display: inline-flex; }
.fp-sale-menu:hover { background: var(--bg-subtle); color: var(--ink-strong); }

/* ⋮ popover ("Assign to Chat") */
.fp-am-backdrop { position: fixed; inset: 0; z-index: 60; }
.fp-am-menu { position: fixed; z-index: 61; background: var(--bg-panel); border: 1px solid var(--border-soft); border-radius: 9px; box-shadow: 0 8px 24px rgba(31, 42, 42, 0.16); padding: 4px; min-width: 150px; }
.fp-am-mi { display: block; width: 100%; text-align: left; border: none; background: none; padding: 7px 11px; border-radius: 6px; font: 500 12.5px 'Inter', sans-serif; color: var(--ink-strong); cursor: pointer; }
.fp-am-mi:hover { background: var(--bg-subtle); }

/* Assign-to-chat modal */
.fp-modal-backdrop { position: fixed; inset: 0; z-index: 70; background: rgba(20, 24, 28, 0.42); display: flex; align-items: center; justify-content: center; padding: 16px; }
.fp-modal { width: 100%; max-width: 380px; max-height: 86vh; overflow-y: auto; background: var(--bg-panel); border-radius: 14px; box-shadow: 0 18px 48px rgba(20, 24, 28, 0.3); padding: 16px 16px 14px; }
.fp-modal-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.fp-modal-title { font: 700 15px 'Libre Baskerville', serif; color: var(--ink-strong); }
.fp-modal-x { border: none; background: none; color: var(--ink-muted); cursor: pointer; padding: 2px; line-height: 0; border-radius: 6px; }
.fp-modal-x:hover { background: var(--bg-subtle); color: var(--ink-strong); }
.fp-modal-hint { font: 400 12px/1.5 'Inter', sans-serif; color: var(--ink-medium); margin: 0 0 12px; }
.fp-modal-lbl { display: block; font: 600 10.5px 'Inter', sans-serif; letter-spacing: .05em; text-transform: uppercase; color: var(--ink-muted); margin: 10px 0 4px; }
.fp-modal-email { font: 600 13px 'Inter', sans-serif; color: var(--ink-strong); background: var(--bg-subtle); border-radius: 8px; padding: 8px 10px; word-break: break-all; }
.fp-modal-search { width: 100%; box-sizing: border-box; border: 1px solid var(--border-soft); border-radius: 8px; padding: 8px 10px; font: 400 13px 'Inter', sans-serif; color: var(--ink-strong); }
.fp-modal-search:focus { outline: none; border-color: var(--accent); }
.fp-modal-chats { margin-top: 6px; max-height: 220px; overflow-y: auto; border: 1px solid var(--border-soft); border-radius: 8px; }
.fp-modal-chats-empty { padding: 14px; text-align: center; color: var(--ink-muted); font-size: 12px; }
.fp-modal-chat { display: flex; align-items: center; gap: 9px; width: 100%; text-align: left; border: none; background: none; padding: 8px 10px; cursor: pointer; border-bottom: 1px solid var(--border-soft); }
.fp-modal-chat:last-child { border-bottom: none; }
.fp-modal-chat:hover { background: var(--bg-subtle); }
.fp-modal-chat.sel { background: rgba(13, 148, 136, 0.10); }
.fp-modal-chat-av { width: 26px; height: 26px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.fp-modal-chat-av--ph { display: inline-flex; align-items: center; justify-content: center; background: var(--accent); color: #fff; font: 700 11px 'Inter', sans-serif; }
.fp-modal-chat-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font: 500 13px 'Inter', sans-serif; color: var(--ink-strong); }
.fp-modal-chat-tick { color: var(--accent-strong); flex-shrink: 0; }
.fp-modal-err { color: #b3261e; font-size: 12px; margin-top: 8px; }
.fp-modal-foot { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }
.fp-modal-cancel { border: 1px solid var(--border-soft); background: var(--bg-panel); color: var(--ink-medium); border-radius: 8px; padding: 7px 14px; font: 500 12.5px 'Inter', sans-serif; cursor: pointer; }
.fp-modal-assign { border: none; background: var(--accent); color: #fff; border-radius: 8px; padding: 7px 16px; font: 600 12.5px 'Inter', sans-serif; cursor: pointer; }
.fp-modal-assign:disabled { opacity: 0.5; cursor: default; }

/* "Earnings from this Chat" tile (This Chat tab) */
.fp-ct-earn { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; background: var(--bg-subtle); border-radius: 10px; padding: 9px 12px; margin-bottom: 4px; }
.fp-ct-earn-lbl { font: 600 10.5px 'Inter', sans-serif; letter-spacing: .05em; text-transform: uppercase; color: var(--ink-muted); }
.fp-ct-earn-val { font: 700 17px 'Inter', sans-serif; color: var(--accent-strong); font-variant-numeric: tabular-nums; }
/* ---- Account view: period + sort controls, custom calendar, Buyers grouping ---- */
.fp-sctl { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 10px 0 8px; }
.fp-period, .fp-sortw { position: relative; }
.fp-pbtn, .fp-sbtn {
  display: inline-flex; align-items: center; gap: 6px; cursor: pointer;
  background: var(--bg-panel); border: 1px solid var(--border-soft); border-radius: 9px;
  padding: 6px 9px; font: 500 11.5px 'Inter', sans-serif; color: var(--ink-medium);
}
.fp-pbtn:hover, .fp-sbtn:hover { border-color: var(--border-medium); }
.fp-pbtn svg:first-child { color: var(--accent, #0d9488); }
.fp-sbtn .mut { color: var(--ink-muted); font-weight: 400; }
.fp-pop {
  position: absolute; top: calc(100% + 5px); left: 0; z-index: 50;
  background: var(--bg-panel); border: 1px solid var(--border-medium); border-radius: 12px;
  box-shadow: 0 6px 24px rgba(31, 42, 42, 0.16); padding: 6px;
}
.fp-menu { width: 168px; }
.fp-menu--r { left: auto; right: 0; }
.fp-mi { padding: 7px 10px; border-radius: 8px; cursor: pointer; font-size: 12.5px; color: var(--ink-medium); white-space: nowrap; }
.fp-mi:hover { background: var(--bg-subtle); }
.fp-mi.cur { color: var(--accent, #0d9488); font-weight: 600; }
.fp-cal { width: 240px; max-width: calc(100vw - 36px); }
.fp-calhead { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.fp-calm { font: 600 12.5px 'Inter', sans-serif; color: var(--ink-strong); }
.fp-navb { background: transparent; border: 1px solid var(--border-soft); border-radius: 7px; padding: 3px 6px; cursor: pointer; color: var(--ink-medium); display: inline-flex; }
.fp-navb:hover { border-color: var(--border-medium); }
.fp-dow { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; font-size: 9.5px; font-weight: 600; color: var(--ink-muted); text-align: center; margin-bottom: 3px; }
.fp-cgrid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.fp-cd { aspect-ratio: 1; display: flex; align-items: center; justify-content: center; font-size: 12px; border-radius: 8px; cursor: pointer; color: var(--ink-strong); }
.fp-cd:hover { background: var(--bg-subtle); }
.fp-cd.blank { visibility: hidden; cursor: default; }
.fp-cd.rng { background: rgba(16, 185, 129, 0.14); color: #047857; border-radius: 0; }
.fp-cd.edge { background: var(--accent, #0d9488); color: #fff; border-radius: 8px; }
.fp-cd.mut { color: var(--border-medium); cursor: default; }
.fp-calfoot { display: flex; align-items: center; justify-content: space-between; margin-top: 10px; font-size: 11.5px; color: var(--ink-muted); }
.fp-apply { background: var(--accent, #0d9488); border: none; border-radius: 8px; color: #fff; font: 600 12px 'Inter', sans-serif; padding: 5px 13px; cursor: pointer; }
.fp-apply:disabled { background: var(--border-medium); cursor: default; }
.fp-buyer { background: var(--bg-panel); border: 1px solid var(--border-soft); border-radius: 10px; box-shadow: 0 1px 2px rgba(31, 42, 42, 0.05); overflow: hidden; }
.fp-buyer-row { display: flex; align-items: center; gap: 7px; width: 100%; text-align: left; cursor: pointer; background: transparent; border: none; padding: 9px 11px; font-family: 'Inter', sans-serif; }
.fp-bchev { color: var(--ink-muted); flex-shrink: 0; transition: transform 0.15s; }
.fp-bchev.open { transform: rotate(90deg); }
.fp-buyer-email { font: 500 12.5px 'Inter', sans-serif; color: var(--ink-strong); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
.fp-buyer-meta { font: 500 11.5px 'Inter', sans-serif; color: var(--ink-muted); flex-shrink: 0; font-variant-numeric: tabular-nums; }
.fp-buyer-detail { padding: 0 8px 8px; display: flex; flex-direction: column; gap: 6px; }
.fp-sale--sub { background: var(--bg-subtle); box-shadow: none; }
.fp-bundle-ph-title {
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: var(--ink-strong);
}
.fp-bundle-ph-sub {
  margin-top: 5px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: var(--ink-muted);
}

// Just-created bundle: brief teal flash so the eye lands on the new card.
// (Parent scoped styles reach the child component's root element.)
.fp-card-just-created {
  animation: fp-new-flash 4.5s ease-out forwards;
}
@keyframes fp-new-flash {
  0%   { background: rgba(16, 185, 129, 0.30); border-color: #047857; box-shadow: 0 0 0 2px rgba(4, 120, 87, 0.35); }
  55%  { background: rgba(16, 185, 129, 0.14); border-color: #047857; box-shadow: 0 0 0 1px rgba(4, 120, 87, 0.2); }
  100% { background: var(--bg-panel); border-color: var(--border-soft); box-shadow: none; }
}

// ===== Create Bundle builder =====
.fp-bundle { display: flex; flex-direction: column; gap: 8px; }
.fp-bundle-bar {
  position: sticky; bottom: 0;
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 8px 2px 2px;
  background: var(--bg-fangate, var(--bg-main));
  border-top: 1px solid var(--border-soft);
}
.fp-bundle-grid {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px;
}
.fp-bundle-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px; overflow: hidden;
  border: 2px solid var(--border-soft);
  background: var(--bg-subtle);
  padding: 0; cursor: pointer;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
  &.selected { border-color: #047857; box-shadow: 0 0 0 1px #047857; }
  &.rejected { opacity: 0.35; cursor: not-allowed; }
}
.fp-bundle-item-type {
  display: flex; align-items: center; justify-content: center;
  width: 100%; height: 100%;
  font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 700;
  color: var(--ink-muted); text-transform: uppercase;
}
.fp-bundle-check {
  position: absolute; top: 3px; left: 3px;
  width: 17px; height: 17px; border-radius: 999px;
  background: #047857; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 800;
}
// Deeplink to the media's source file — top-right, click doesn't toggle select.
.fp-bundle-deeplink {
  position: absolute; top: 3px; right: 3px;
  width: 18px; height: 18px; border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(17, 24, 24, 0.55); color: #fff;
  opacity: 0.85; transition: opacity 0.12s, background 0.12s;
  cursor: pointer;
  &:hover { opacity: 1; background: #047857; }
}
.fp-bundle-status {
  position: absolute; left: 3px; bottom: 3px;
  padding: 1px 5px; border-radius: 5px;
  background: rgba(180, 83, 9, 0.92); color: #fff;
  font-family: 'Inter', sans-serif; font-size: 8.5px; font-weight: 700;
  text-transform: uppercase;
}
.fp-bundle-more {
  padding: 7px 10px; border-radius: 8px;
  border: 1px solid var(--border-soft); background: var(--bg-panel);
  color: #047857; font-family: 'Inter', sans-serif; font-size: 11.5px; font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) { background: rgba(16, 185, 129, 0.08); }
  &:disabled { opacity: 0.55; }
}
// Settings popup (step 2)
.fp-bm-overlay {
  position: fixed; inset: 0; z-index: 10000;
  display: flex; align-items: center; justify-content: center;
  padding: 18px;
  background: rgba(18, 22, 22, 0.46);
  backdrop-filter: blur(2px);
}
.fp-bm-modal {
  width: 100%; max-width: 380px;
  display: flex; flex-direction: column; gap: 7px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  padding: 16px 16px 14px;
  box-shadow: 0 18px 48px rgba(31, 42, 42, 0.22);
}
.fp-bm-head { display: flex; align-items: center; justify-content: space-between; }
.fp-bm-title {
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 15px; font-weight: 700;
  color: var(--ink-strong);
}
.fp-bm-close {
  border: none; background: transparent;
  font-size: 18px; line-height: 1; cursor: pointer;
  color: var(--ink-muted);
  &:hover { color: var(--ink-strong); }
}
.fp-bm-sub {
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 700;
  color: #047857;
}
.fp-bundle-count {
  font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 700;
  color: var(--ink-medium);
}
.fp-bundle-input {
  padding: 7px 9px; border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel); color: var(--ink-strong);
  font-family: 'Inter', sans-serif; font-size: 12px;
  &:focus { outline: none; border-color: #047857; }
}
.fp-bundle-flag {
  display: flex; align-items: center; gap: 7px;
  font-family: 'Inter', sans-serif; font-size: 11.5px; color: var(--ink-medium);
  cursor: pointer;
  input { accent-color: #047857; }
}
// Softer-coloured parenthetical hint after a flag label.
.fp-flag-hint { color: var(--ink-muted); font-weight: 400; }
// Co-creator tagging block (Create Bundle popup)
.fp-bundle-cotag {
  display: flex; flex-direction: column; gap: 6px;
  padding: 8px 10px; border-radius: 9px;
  border: 1px solid var(--border-soft); background: var(--bg-subtle);
}
.fp-bundle-cotag-note { font-size: 11px; line-height: 1.45; color: var(--ink-muted); }
.fp-bundle-cotag-chips { display: flex; flex-wrap: wrap; gap: 5px; }
.fp-bundle-cotag-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 600;
  padding: 2px 4px 2px 8px; border-radius: 999px;
  background: var(--accent-tint, rgba(13, 148, 136, 0.12));
  color: var(--accent-strong, #0f766e);
}
.fp-bundle-cotag-x {
  border: none; background: transparent; cursor: pointer; line-height: 1;
  font-size: 14px; color: var(--accent-strong, #0f766e);
}
.fp-bundle-cotag-hint { font-size: 11px; color: var(--ink-muted); }
.fp-bundle-cotag-quick { display: flex; flex-direction: column; gap: 4px; }
.fp-bundle-cotag-quick-label { font-size: 10.5px; color: var(--ink-muted); }
.fp-bundle-cotag-chip--pick {
  border: 1px dashed var(--accent-strong, #0f766e); background: transparent;
  cursor: pointer; padding: 2px 8px;
  &:hover { background: var(--accent-tint, rgba(13, 148, 136, 0.12)); }
}
.fp-bundle-cotag-results {
  display: flex; flex-direction: column;
  border: 1px solid var(--border-soft); border-radius: 8px; overflow: hidden;
  background: var(--bg-panel);
}
.fp-bundle-cotag-result {
  display: flex; align-items: baseline; gap: 8px;
  padding: 6px 9px; border: none; background: transparent; cursor: pointer;
  text-align: left; width: 100%;
  &:not(:last-child) { border-bottom: 1px solid var(--border-soft); }
  &:hover { background: var(--bg-subtle); }
}
.fp-bundle-cotag-result-name { font-size: 12.5px; font-weight: 600; color: var(--ink-strong); }
.fp-bundle-cotag-result-email { font-size: 11px; color: var(--ink-muted); }
// Post-create "in review" notice body.
.fp-review-text {
  margin: 2px 0 4px;
  font-family: 'Inter', sans-serif; font-size: 12px; line-height: 1.5;
  color: var(--ink-medium);
  strong { color: var(--ink-strong); }
}
.fp-bundle-avs {
  font-family: 'Inter', sans-serif; font-size: 10.5px; color: var(--ink-muted);
}
.fp-bundle-create {
  padding: 9px 12px; border-radius: 8px; border: none;
  background: #047857; color: #fff;
  font-family: 'Inter', sans-serif; font-size: 12.5px; font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) { background: #065f46; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.fp-live-toggle-dot {
  width: 8px; height: 8px; border-radius: 999px;
  background: #d1d5db;
  border: 1px solid rgba(0,0,0,0.12);
  flex-shrink: 0;
  transition: background 0.12s;
  &.on { background: #10b981; border-color: #047857; }
}

// Page navigation — compact numbered buttons + prev/next arrows. Top row
// sits inline with the Live-only toggle; bottom variant sits after the last
// card, right-aligned, so users don't scroll back up after reading.
.fp-pager {
  display: inline-flex; align-items: center; gap: 3px;
}
.fp-pager--bottom {
  display: flex; justify-content: flex-end;
  margin-top: 6px;
  padding-right: 0;
}
.fp-pager-btn,
.fp-pager-num {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 22px; height: 22px;
  padding: 0 5px;
  border-radius: 6px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 700;
  cursor: pointer;
  transition: all 0.12s;
  &:hover:not(:disabled):not(.active) {
    border-color: #047857;
    color: #047857;
    background: rgba(16, 185, 129, 0.08);
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid #047857; outline-offset: 1px; }
}
.fp-pager-num.active {
  background: #047857;
  border-color: #047857;
  color: #fff;
  // User asked for explicit bold emphasis on the current page.
  font-weight: 900;
}
// Ellipsis between gaps — non-clickable spacer.
.fp-pager-gap {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 14px; height: 22px;
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 700;
  color: var(--ink-muted);
  user-select: none;
}
</style>
